/* ============================================
   ROOM DESIGNER — Mobile Module
   Asset file: custom-room-designer-mobile.js

   Handles mobile toolbar, joystick d-pad,
   handle activation/cycling/nudging, door wall-following,
   mobile auto-place, and floating buttons.
   Loaded before custom-room-designer.js via <script defer>.
   Instantiated by the RoomDesigner constructor.
   ============================================ */

class RoomDesignerMobile {
  constructor(rd) {
    this.rd = rd;
    this._installMethods();

    // Run mobile setup (previously called from init()).
    // Call via rd so 'this' inside each method = rd (the RoomDesigner instance).
    rd._setupMobileToolbar();
    rd._setupJoystick();
    rd._setupFloatingDelete();
    rd._setupFloatingCancel();
    rd._setupFloatingRepeat();
  }

  _installMethods() {
    const rd = this.rd;
    // Bind each method onto the RD instance so existing call sites
    // (this._setupMobileToolbar(), this._activateSelectHandle(), etc.) keep working.
    // .bind(rd) ensures 'this' inside each method refers to the RD instance.
    rd._setupMobileToolbar = this._setupMobileToolbar.bind(rd);
    rd._updateToolbarSpacers = this._updateToolbarSpacers.bind(rd);
    rd._updateMobilePopoverState = this._updateMobilePopoverState.bind(rd);
    rd._dismissMobilePopovers = this._dismissMobilePopovers.bind(rd);
    rd._startPopoverIdlePulse = this._startPopoverIdlePulse.bind(rd);
    rd._clearPopoverIdlePulse = this._clearPopoverIdlePulse.bind(rd);
    rd._applyPopoverIdlePulse = this._applyPopoverIdlePulse.bind(rd);
    rd._setupFloatingDelete = this._setupFloatingDelete.bind(rd);
    rd._setupFloatingCancel = this._setupFloatingCancel.bind(rd);
    rd._tapFlash = this._tapFlash.bind(rd);
    rd._updateFloatingDelete = this._updateFloatingDelete.bind(rd);
    rd._updateFloatingCancel = this._updateFloatingCancel.bind(rd);
    rd._setupFloatingRepeat = this._setupFloatingRepeat.bind(rd);
    rd._updateFloatingRepeat = this._updateFloatingRepeat.bind(rd);
    rd._handleRepeatAction = this._handleRepeatAction.bind(rd);
    rd._repeatDoorPlace = this._repeatDoorPlace.bind(rd);
    rd._updateToolbarEdgeEffect = this._updateToolbarEdgeEffect.bind(rd);
    rd._setupJoystick = this._setupJoystick.bind(rd);
    rd._resetDoorMobileState = this._resetDoorMobileState.bind(rd);
    rd._startCheckPulse = this._startCheckPulse.bind(rd);
    rd._clearCheckPulse = this._clearCheckPulse.bind(rd);
    rd._startArrowPulse = this._startArrowPulse.bind(rd);
    rd._clearArrowPulse = this._clearArrowPulse.bind(rd);
    rd._restartJoystickPulse = this._restartJoystickPulse.bind(rd);
    rd._setJoystickArrowsEnabled = this._setJoystickArrowsEnabled.bind(rd);
    rd._setJoystickArrowsDirs = this._setJoystickArrowsDirs.bind(rd);
    rd._getDoorWallArrows = this._getDoorWallArrows.bind(rd);
    rd._arrowToWallDelta = this._arrowToWallDelta.bind(rd);
    rd._isEntireRoomSelected = this._isEntireRoomSelected.bind(rd);
    rd._shouldAutoJoystick = this._shouldAutoJoystick.bind(rd);
    rd._getWallGrabPerpArrows = this._getWallGrabPerpArrows.bind(rd);
    rd._getResizeArrows = this._getResizeArrows.bind(rd);
    rd._buildHandleCycleList = this._buildHandleCycleList.bind(rd);
    rd._isCycleHighlighted = this._isCycleHighlighted.bind(rd);
    rd._evaluateSelectJoystickMode = this._evaluateSelectJoystickMode.bind(rd);
    rd._activateSelectHandleFromCycle = this._activateSelectHandleFromCycle.bind(rd);
    rd._activateSelectHandle = this._activateSelectHandle.bind(rd);
    rd._deactivateSelectHandle = this._deactivateSelectHandle.bind(rd);
    rd._nudgeSelectHandle = this._nudgeSelectHandle.bind(rd);
    rd._enterRotateDial = this._enterRotateDial.bind(rd);
    rd._updateRotateDialIndicator = this._updateRotateDialIndicator.bind(rd);
    rd._exitRotateDial = this._exitRotateDial.bind(rd);
    rd._moveDoorAlongWall = this._moveDoorAlongWall.bind(rd);
    rd._slideSelectedDoorAlongWall = this._slideSelectedDoorAlongWall.bind(rd);
    rd._findNextWallAtCorner = this._findNextWallAtCorner.bind(rd);
    rd._updateDoorMobileArrows = this._updateDoorMobileArrows.bind(rd);
    rd._updateDoorSwingArrows = this._updateDoorSwingArrows.bind(rd);
    rd._cycleDoorEditSwing = this._cycleDoorEditSwing.bind(rd);
    rd._updateDoorEditSwingArrows = this._updateDoorEditSwingArrows.bind(rd);
    rd._drawDoorEditSwingMobile = this._drawDoorEditSwingMobile.bind(rd);
    rd._getDoorSwingDots = this._getDoorSwingDots.bind(rd);
    rd._doorMobileConfirmHinge = this._doorMobileConfirmHinge.bind(rd);
    rd._doorMobileConfirmEnd = this._doorMobileConfirmEnd.bind(rd);
    rd._doorMobileCycleSwing = this._doorMobileCycleSwing.bind(rd);
    rd._doorMobileConfirmSwing = this._doorMobileConfirmSwing.bind(rd);
    rd._doorMobilePlaceDoor = this._doorMobilePlaceDoor.bind(rd);
    rd._doorMobileFinish = this._doorMobileFinish.bind(rd);
    rd._enterDoorIdleState = this._enterDoorIdleState.bind(rd);
    rd._isDoorIdleState = this._isDoorIdleState.bind(rd);
    rd._updateRectCheckmark = this._updateRectCheckmark.bind(rd);
    rd._updateFreehandCheckmark = this._updateFreehandCheckmark.bind(rd);
    rd._getMobileDotScreenPos = this._getMobileDotScreenPos.bind(rd);
    rd._centerGridForMobile = this._centerGridForMobile.bind(rd);
    rd._mobilePanCompensate = this._mobilePanCompensate.bind(rd);
    rd._mobilePanToWorldPoint = this._mobilePanToWorldPoint.bind(rd);
    rd._cancelPanAnimation = this._cancelPanAnimation.bind(rd);
    rd._getCycleItemWorldPos = this._getCycleItemWorldPos.bind(rd);
    rd._mobileRectAutoPlace = this._mobileRectAutoPlace.bind(rd);
    rd._mobileFreehandAutoPlace = this._mobileFreehandAutoPlace.bind(rd);
    rd._mobileDoorAutoPlace = this._mobileDoorAutoPlace.bind(rd);
    rd._mobileObjectAutoPlace = this._mobileObjectAutoPlace.bind(rd);
    rd._animateRectIntro = this._animateRectIntro.bind(rd);
    rd._animateRectLock = this._animateRectLock.bind(rd);
    rd._updateJoystickVisibility = this._updateJoystickVisibility.bind(rd);
  }

  /* ── MOBILE TOOLBAR REARRANGEMENT ── */

  _setupMobileToolbar() {
    if (window.innerWidth > 480) return;
    // Skip entire toolbar setup in view-only mode (toolbar is hidden)
    // _viewOnly isn't set yet during construction, so check the synchronous class
    if (this.canvas.closest('.room-designer').classList.contains('view-only-pending')) return;
    this._isMobileToolbar = true;
    const toolControls = document.querySelector('.tool-controls');
    const settingsPanel = document.querySelector('.settings-panel');
    if (!toolControls || !settingsPanel) return;

    const settingsBtn = document.getElementById('settingsToggle');
    const undoBtn = document.getElementById('undoBtnMobile');
    const redoBtn = document.getElementById('redoBtnMobile');
    const settingsDrawer = document.getElementById('settingsDrawer');

    // Move settings + designs buttons to top-right corner (next to status bar)
    const designsBtn = document.getElementById('designsToggle');
    if (settingsBtn) {
      settingsBtn.classList.add('mobile-top-right');
      this.container.appendChild(settingsBtn);
    }
    if (designsBtn) {
      designsBtn.classList.add('mobile-top-right', 'mobile-top-right-second');
      this.container.appendChild(designsBtn);
    }

    // Build mobile toolbar: fixed undo/redo | divider | scrollable tools
    const mobileToolbar = document.createElement('div');
    mobileToolbar.className = 'mobile-toolbar';

    // Fixed section: undo + redo
    const fixedSection = document.createElement('div');
    fixedSection.className = 'toolbar-fixed';
    [undoBtn, redoBtn].forEach(btn => {
      if (btn) {
        btn.classList.add('tool-btn-mobile');
        fixedSection.appendChild(btn);
      }
    });

    // Divider
    const divider = document.createElement('div');
    divider.className = 'toolbar-divider';

    // Wrap tool-controls as the scrollable section
    mobileToolbar.appendChild(fixedSection);
    mobileToolbar.appendChild(divider);

    // Add spacers so edge buttons can scroll past the shrink zone
    const spacerStart = document.createElement('div');
    spacerStart.className = 'toolbar-spacer';
    toolControls.insertBefore(spacerStart, toolControls.firstChild);
    const spacerEnd = document.createElement('div');
    spacerEnd.className = 'toolbar-spacer';
    toolControls.appendChild(spacerEnd);
    this._toolbarSpacerStart = spacerStart;
    this._toolbarSpacerEnd = spacerEnd;
    this._updateToolbarSpacers();

    // Insert tool-controls into the mobile toolbar wrapper
    mobileToolbar.appendChild(toolControls);

    // Place the wrapper into the canvas container
    this.container.appendChild(mobileToolbar);

    // Move settings drawer to canvas-container so it's still accessible
    if (settingsDrawer) {
      settingsDrawer.classList.add('mobile-drawer');
      this.container.appendChild(settingsDrawer);
    }

    // Move designs drawer to canvas-container for mobile
    const designsDrawer = document.getElementById('designsDrawer');
    if (designsDrawer) {
      designsDrawer.classList.add('mobile-drawer');
      this.container.appendChild(designsDrawer);
    }

    // Move popovers to canvas-container so position:absolute works relative to canvas
    const penPop = document.getElementById('penPopover');
    const objPop = document.getElementById('objectsPopover');
    const zoomPop = document.getElementById('zoomPopover');
    [penPop, objPop, zoomPop].forEach(pop => {
      if (pop) this.container.appendChild(pop);
    });

    // Reset popover idle pulse on any interaction inside popover panels or settings
    [penPop, objPop, zoomPop, settingsDrawer, designsDrawer].forEach(el => {
      if (el) el.addEventListener('click', () => { this._clearPopoverIdlePulse(); this._startPopoverIdlePulse(); }, true);
    });

    // Hide the now-empty settings panel
    settingsPanel.classList.add('mobile-hidden');

    // Hide standalone eraser button on mobile; erase is in pen popover
    const eraserBtn = document.getElementById('eraserBtn');
    if (eraserBtn) eraserBtn.style.display = 'none';

    // Show the erase button inside the pen popover method row on mobile
    const penEraseBtn = document.querySelector('.pen-erase-mode-btn');
    if (penEraseBtn) penEraseBtn.style.display = '';

    // Scroll-wheel edge effect: buttons at edges shrink like a curved carousel
    this._toolbarEl = toolControls;
    this._updateToolbarEdgeEffect();
    toolControls.addEventListener('scroll', () => this._updateToolbarEdgeEffect(), { passive: true });
    // Also run on resize in case orientation changes
    window.addEventListener('resize', () => {
      if (window.innerWidth <= 480) {
        this._updateToolbarEdgeEffect();
        this._updateToolbarSpacers();
      }
    });

    // Track which mobile popover is open (blocks canvas taps)
    this._mobilePopoverOpen = false;

    // Auto-peek: smooth scroll right → left → settle at center (skip in view-only)
    if (this._viewOnly) return;
    setTimeout(() => {
      const maxScroll = toolControls.scrollWidth - toolControls.clientWidth;
      const center = Math.round(maxScroll / 2);
      const peekRight = Math.min(maxScroll, center + 80);
      const peekLeft = Math.max(0, center - 80);
      // Keyframes: [time 0-1, scroll position]
      const keyframes = [
        [0, 0],
        [0.33, peekRight],
        [0.67, peekLeft],
        [1, center]
      ];
      const duration = 1400;
      const startTime = performance.now();
      const ease = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; // cubic ease-in-out
      const animate = (now) => {
        const elapsed = now - startTime;
        const rawT = Math.min(elapsed / duration, 1);
        // Find which keyframe segment we're in
        let segStart = keyframes[0], segEnd = keyframes[1];
        for (let i = 1; i < keyframes.length; i++) {
          if (rawT <= keyframes[i][0]) { segStart = keyframes[i - 1]; segEnd = keyframes[i]; break; }
        }
        // Local progress within this segment, eased
        const segT = (rawT - segStart[0]) / (segEnd[0] - segStart[0]);
        const easedT = ease(Math.max(0, Math.min(1, segT)));
        const scrollPos = segStart[1] + (segEnd[1] - segStart[1]) * easedT;
        toolControls.scrollLeft = Math.round(scrollPos);
        if (rawT < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }, 600);

    // Scroll hint tooltip — skip in view-only mode (toolbar is hidden)
    // Check view-only-pending class since _viewOnly flag isn't set yet during construction
    if (!this.canvas.closest('.room-designer').classList.contains('view-only-pending')) {
      const hint = document.createElement('div');
      hint.className = 'toolbar-scroll-hint';
      hint.textContent = '\u2190 Scroll for all tools \u2192';
      // Position centered above the scrollable area (not the fixed undo/redo)
      const tcRect = toolControls.getBoundingClientRect();
      const cRect = this.container.getBoundingClientRect();
      const scrollCenter = tcRect.left - cRect.left + tcRect.width / 2;
      hint.style.left = scrollCenter + 'px';
      this.container.appendChild(hint);
      // Show after peek animation completes (600ms delay + 1400ms animation + 200ms buffer)
      setTimeout(() => {
        hint.classList.add('visible');
        // Only start listening for dismiss AFTER hint is shown
        const dismissHint = () => {
          hint.classList.remove('visible');
          setTimeout(() => hint.remove(), 300);
          localStorage.setItem('rd_toolbar_hint_seen', '1');
          toolControls.removeEventListener('scroll', dismissHint);
        };
        toolControls.addEventListener('scroll', dismissHint, { passive: true });
        setTimeout(dismissHint, 4000);
      }, 2200);
    }
  }

  _updateToolbarSpacers() {
    if (!this._toolbarSpacerStart || !this._toolbarSpacerEnd) return;
    this._toolbarSpacerStart.style.width = '30px';
    this._toolbarSpacerEnd.style.width = '30px';
  }


  _updateMobilePopoverState() {
    if (!this._isMobileToolbar) return;
    // Check if any popover is visible
    const penVis = document.getElementById('penPopover').classList.contains('visible');
    const objVis = document.getElementById('objectsPopover').classList.contains('visible');
    const zoomVis = document.getElementById('zoomPopover').classList.contains('visible');
    const settingsVis = document.getElementById('settingsDrawer').classList.contains('open');
    const designsVis = document.getElementById('designsDrawer') ? document.getElementById('designsDrawer').classList.contains('open') : false;
    this._mobilePopoverOpen = penVis || objVis || zoomVis || settingsVis || designsVis;

    // Highlight parent buttons blue — when a popover is open, only that popover's button is blue
    const penBtn = document.getElementById('penToolBtn');
    const objBtn = document.getElementById('objectsToolBtn');
    const zoomBtn = document.getElementById('zoomToggleBtn');
    const settBtn = document.getElementById('settingsToggle');
    const desBtn = document.getElementById('designsToggle');
    const handBtn = document.getElementById('handToolBtn');
    const selBtn = document.getElementById('selectToolBtn');
    if (this._mobilePopoverOpen) {
      // A popover is open — only highlight that popover's button, clear everything else
      if (penBtn) penBtn.classList.toggle('active', penVis);
      if (objBtn) objBtn.classList.toggle('active', objVis);
      if (zoomBtn) zoomBtn.classList.toggle('active', zoomVis);
      if (settBtn) settBtn.classList.toggle('active', settingsVis);
      if (desBtn) desBtn.classList.toggle('active', designsVis);
      if (handBtn) handBtn.classList.remove('active');
      if (selBtn) selBtn.classList.remove('active');
      // Start popover idle pulse timer (resets on each state change)
      this._startPopoverIdlePulse();
    } else {
      // No popover open — highlight based on active tool
      if (penBtn) penBtn.classList.toggle('active', this.activeTool === 'pen' || this.activeTool === 'eraser');
      if (objBtn) objBtn.classList.toggle('active', this.activeTool === 'door');
      if (zoomBtn) zoomBtn.classList.toggle('active', !!this._zoomFitPanMode);
      if (settBtn) settBtn.classList.remove('active');
      if (desBtn) desBtn.classList.remove('active');
      // No popover open — clear any idle pulse
      this._clearPopoverIdlePulse();
    }
  }

  _dismissMobilePopovers() {
    if (!this._isMobileToolbar) return;
    // Blur any focused input inside a popover so the mobile keyboard closes
    const ae = document.activeElement;
    if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'SELECT') &&
        ae.closest('#penPopover, #objectsPopover, #zoomPopover, #settingsDrawer, #designsDrawer')) {
      ae.blur();
    }
    ['penPopover', 'objectsPopover', 'zoomPopover'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.classList.remove('visible');
      clearTimeout(this._popoverTimers[id]);
    });
    const sd = document.getElementById('settingsDrawer');
    const st = document.getElementById('settingsToggle');
    if (sd) sd.classList.remove('open');
    if (st) st.classList.remove('open');
    const dd = document.getElementById('designsDrawer');
    const dt = document.getElementById('designsToggle');
    if (dd) dd.classList.remove('open');
    if (dt) dt.classList.remove('open');
    this._pinnedPopover = null;
    if (this._syncDrawerPins) this._syncDrawerPins();
    // Clear rect pulse hint when dismissing popovers
    if (this._rectPulseTimer) { clearTimeout(this._rectPulseTimer); this._rectPulseTimer = null; }
    const penBtn = document.getElementById('penToolBtn');
    if (penBtn) penBtn.classList.remove('rect-pulse-hint', 'rect-pulse-subtract');
    // Clear popover idle pulse
    this._clearPopoverIdlePulse();
    // Restart appropriate joystick pulse timer if we're still waiting for action
    this._restartJoystickPulse();
    this._updateMobilePopoverState();
  }

  /* ── POPOVER IDLE PULSE ──
     When a pop-out menu is open on mobile and no screen activity for 4s,
     pulse the parent button to prompt the user to interact.
     Exceptions for draw/object menus: if only the top row is selected
     but the second row hasn't been picked, pulse the 2nd-row options instead. */

  _startPopoverIdlePulse() {
    if (!this._isMobileToolbar) return;
    this._clearPopoverIdlePulse();
    this._popoverIdleTimer = setTimeout(() => {
      this._applyPopoverIdlePulse();
    }, 4000);
  }

  _clearPopoverIdlePulse() {
    if (this._popoverIdleTimer) { clearTimeout(this._popoverIdleTimer); this._popoverIdleTimer = null; }
    // Clear all possible idle pulse targets
    const penBtn = document.getElementById('penToolBtn');
    const objBtn = document.getElementById('objectsToolBtn');
    const zoomBtn = document.getElementById('zoomToggleBtn');
    const settBtn = document.getElementById('settingsToggle');
    [penBtn, objBtn, zoomBtn, settBtn].forEach(b => { if (b) b.classList.remove('popover-idle-pulse'); });
    // Clear sub-option pulses
    document.querySelectorAll('.pen-mode-btn, .pen-draw-mode-btn, .objects-type-btn, .door-type-btn').forEach(b => b.classList.remove('popover-idle-pulse'));
  }

  _applyPopoverIdlePulse() {
    if (!this._isMobileToolbar) return;
    const penVis = document.getElementById('penPopover').classList.contains('visible');
    const objVis = document.getElementById('objectsPopover').classList.contains('visible');
    const zoomVis = document.getElementById('zoomPopover').classList.contains('visible');
    const settingsVis = document.getElementById('settingsDrawer').classList.contains('open');
    if (!penVis && !objVis && !zoomVis && !settingsVis) return;

    // Draw menu exception: check if we need to pulse sub-options instead of parent
    if (penVis) {
      // Row 1 (Method) not yet selected — pen popover just opened with nothing picked
      if (!this._penToolClicked) {
        // Pulse the method buttons (Rectangle / Freehand / Erase) — skip disabled
        document.querySelectorAll('.pen-draw-mode-btn, .pen-erase-mode-btn').forEach(b => { if (!b.classList.contains('disabled') && !b.classList.contains('disabled-with-tooltip')) b.classList.add('popover-idle-pulse'); });
        return;
      }
      // Row 1 selected but Row 2 (Action: Add/Subtract) not yet picked
      const actionVisible = document.getElementById('penActionOptions').style.display !== 'none';
      if (actionVisible && !this._mobileActionChosen) {
        // Pulse the action buttons (Add / Subtract)
        document.querySelectorAll('.pen-mode-btn').forEach(b => {
          if (b.dataset.value !== 'erase') b.classList.add('popover-idle-pulse');
        });
        return;
      }
      // All rows selected — pulse the parent button
      const penBtn = document.getElementById('penToolBtn');
      if (penBtn) penBtn.classList.add('popover-idle-pulse');
      return;
    }

    // Object menu exception: check if we need to pulse sub-options instead of parent
    if (objVis) {
      // Row 1 (Type) not yet selected — object popover just opened with nothing picked
      if (!this._doorToolClicked) {
        // Pulse the type buttons (Door / Column / Pillar)
        document.querySelectorAll('.objects-type-btn').forEach(b => b.classList.add('popover-idle-pulse'));
        return;
      }
      // Row 1 selected as Door but Row 2 (Door Style) not yet picked
      if (this.objectType === 'door' && !this._mobileObjectChosen) {
        document.querySelectorAll('.door-type-btn').forEach(b => b.classList.add('popover-idle-pulse'));
        return;
      }
      // All rows selected (or type is column/pillar which auto-completes) — pulse the parent
      const objBtn = document.getElementById('objectsToolBtn');
      if (objBtn) objBtn.classList.add('popover-idle-pulse');
      return;
    }

    // Zoom & Settings — no exceptions, always pulse the parent button
    if (zoomVis) {
      const zoomBtn = document.getElementById('zoomToggleBtn');
      if (zoomBtn) zoomBtn.classList.add('popover-idle-pulse');
    }
    if (settingsVis) {
      const settBtn = document.getElementById('settingsToggle');
      if (settBtn) settBtn.classList.add('popover-idle-pulse');
    }
  }

  _setupFloatingDelete() {
    const btn = document.getElementById('floatingDeleteBtn');
    if (!btn) return;
    this._floatingDeleteBtn = btn;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const hasFloor = this._floorMode && window.roomDesignerOverlay && window.roomDesignerOverlay._floorSelections.length > 0;
      const hasObjects = this.selectedObjects.size > 0 || this.selectedDoors.size > 0 || this.selectedWalls.size > 0;
      this._deleteWithEquipmentCheck(hasFloor, hasObjects);
    });
    btn.addEventListener('touchstart', (e) => {
      e.stopPropagation(); // Prevent canvas touch handler from firing
    }, { passive: true });
  }

  _setupFloatingCancel() {
    const btn = document.getElementById('floatingCancelBtn');
    if (!btn) return;
    this._floatingCancelBtn = btn;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.activeTool === 'pen' && this.mode === 'draw' && this._isMobileToolbar) {
        // Cancel pen/draw on mobile — clear in-progress drawing, return to idle
        this.saveUndoState();

        // Cancel rectangle state
        this._resetRectState();

        // Cancel freehand state — discard in-progress walls
        if (this._freehandAutoPlaced || this.hasPlacedFirstPoint) {
          this.currentWall = null;
          this.hasPlacedFirstPoint = false;
          this._resetFreehandState();
          if (this.currentRoom && this.currentRoom.walls.length > 0) {
            // Discard all in-progress walls from this drawing
            this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
          }
        }

        // Clear mouse position so the cursor dot doesn't linger
        this.mouseWorld = null;
        this.mouseWorldRaw = null;

        // Reset mobile action state — user must re-select draw options
        this._mobileActionChosen = false;
        this._penCancelledIdle = true;

        // Hide joystick, dismiss popover, keep pen button blue
        this._updateJoystickVisibility();
        this._dismissMobilePopovers();
        document.getElementById('penToolBtn').classList.add('active');

        // Update UI
        this._updateFloatingCancel();
        this._updateFloatingRepeat();
        this.updateMeasurements();
        this.updateStatus('Drawing cancelled.');
        this.render();
        return;
      }
      if (this.activeTool === 'select') {
        // Deselect all and hide joystick
        this.selectedWalls = new Set(); this.selectedDoors = new Set(); this.selectedObjects = new Set();
        this._selectHandleMode = null;
        this._updateJoystickVisibility();
        this._updateFloatingDelete();
        this._updateFloatingCancel();
        this._updateFloatingRepeat();
        this._updateSelectionStatus();
        this.render();
      } else {
        // Cancel unplaced preview — enter idle state
        this._enterDoorIdleState();
        this._updateFloatingCancel();
        this._updateFloatingRepeat();
        this.render();
      }
    });
    btn.addEventListener('touchstart', (e) => {
      e.stopPropagation();
    }, { passive: true });
  }

  _tapFlash(btn) {
    btn.classList.add('tap-flash');
    setTimeout(() => btn.classList.remove('tap-flash'), 150);
  }

  _updateFloatingDelete() {
    const hasSelection = this.selectedWalls.size > 0 || this.selectedDoors.size > 0 || this.selectedObjects.size > 0;
    const isSelectTool = this.activeTool === 'select';
    const hasFloorSelection = this._floorMode && window.roomDesignerOverlay && window.roomDesignerOverlay._floorSelections.length > 0;
    const showDelete = (hasSelection && isSelectTool) || hasFloorSelection;
    // Mobile floating button
    if (this._floatingDeleteBtn) {
      this._floatingDeleteBtn.classList.toggle('visible', showDelete);
      // Re-position cancel button: stack above delete when both visible, otherwise bottom slot
      if (this._floatingCancelBtn && this._floatingCancelBtn.classList.contains('visible')) {
        const stackedPos = this.largeUI ? '137px' : '124px';
        const soloPos = this.largeUI ? '80px' : '72px';
        this._floatingCancelBtn.style.bottom = showDelete ? stackedPos : soloPos;
      }
    }
    // Desktop delete button
    if (this._desktopDeleteBtn) {
      this._desktopDeleteBtn.classList.toggle('visible', showDelete);
    }
  }

  _updateFloatingCancel() {
    if (!this._floatingCancelBtn) return;
    const hasPreview = this.activeTool === 'door' && !this._isDoorIdleState() &&
      (this.mouseWorldRaw || this._doorMobile);
    const hasSelection = this.activeTool === 'select' &&
      (this.selectedWalls.size > 0 || this.selectedDoors.size > 0 || this.selectedObjects.size > 0);
    const hasPenDraw = this._isMobileToolbar && this.activeTool === 'pen' && this.mode === 'draw' &&
      (this._rectCorner1 || this._freehandAutoPlaced || this.hasPlacedFirstPoint);
    const showCancel = !!(hasPreview || hasSelection || hasPenDraw);
    this._floatingCancelBtn.classList.toggle('visible', showCancel);
    // Position: if delete is also visible, stack above it; otherwise take the bottom slot
    if (showCancel) {
      const deleteVisible = this._floatingDeleteBtn && this._floatingDeleteBtn.classList.contains('visible');
      const stackedPos = this.largeUI ? '137px' : '124px';
      const soloPos = this.largeUI ? '80px' : '72px';
      this._floatingCancelBtn.style.bottom = deleteVisible ? stackedPos : soloPos;
    }
  }

  _setupFloatingRepeat() {
    const btn = document.getElementById('floatingRepeatBtn');
    if (!btn) return;
    this._floatingRepeatBtn = btn;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._handleRepeatAction();
    });
    btn.addEventListener('touchstart', (e) => {
      e.stopPropagation();
    }, { passive: true });
  }

  _updateFloatingRepeat() {
    if (!this._floatingRepeatBtn) return;
    const show = !!(this._showRepeatButton && this._lastPlacedObject && this._isMobileToolbar);
    this._floatingRepeatBtn.classList.toggle('visible', show);
    if (show) {
      // Count visible buttons below to determine stacking slot
      const deleteVisible = this._floatingDeleteBtn && this._floatingDeleteBtn.classList.contains('visible');
      const cancelVisible = this._floatingCancelBtn && this._floatingCancelBtn.classList.contains('visible');
      let slot = 0;
      if (deleteVisible) slot++;
      if (cancelVisible) slot++;
      // slot 0 → bottom position, slot 1 → middle, slot 2 → top
      const positions = this.largeUI ? ['80px', '137px', '194px'] : ['72px', '124px', '176px'];
      this._floatingRepeatBtn.style.bottom = positions[slot];
    }
  }

  _handleRepeatAction() {
    if (!this._lastPlacedObject) return;
    const saved = this._lastPlacedObject;
    // Hide repeat button
    this._showRepeatButton = false;
    this._updateFloatingRepeat();
    // Set core tool state
    this.activeTool = 'door';
    this.mode = 'door';
    this._doorToolClicked = true;
    this._mobileObjectChosen = true;
    // Keep objects toolbar button active
    document.getElementById('objectsToolBtn').classList.add('active');
    if (saved.objectType === 'door') {
      // Restore door settings
      this.doorType = saved.doorType;
      this.doorWidth = saved.doorWidth;
      // Activate the matching door type button visually
      document.querySelectorAll('.door-type-btn').forEach(b => b.classList.toggle('active', b.dataset.door === saved.doorType));
      document.querySelectorAll('.objects-type-btn').forEach(b => b.classList.toggle('active', b.dataset.type === 'door'));
      this._repeatDoorPlace(saved);
    } else {
      // Restore column/pillar settings
      this.objectType = saved.objectType;
      if (saved.objectType === 'rectangle') {
        this.rectWidth = saved.rectWidth;
        this.rectDepth = saved.rectDepth;
      } else {
        this.circleDiameter = saved.circleDiameter;
      }
      // Activate the matching object type button visually
      document.querySelectorAll('.objects-type-btn').forEach(b => b.classList.toggle('active', b.dataset.type === saved.objectType));
      // Place 1' down and 1' right from last placed position
      const offsetX = saved.x + 1;
      const offsetY = saved.y + 1;
      const snapped = this.snapToGrid(offsetX, offsetY);
      const target = this._getMobileDotScreenPos();
      const scale = this.basePixelsPerFoot * this.zoomLevel;
      this.panX = target.x - snapped.x * scale;
      this.panY = target.y - snapped.y * scale;
      this.mouseWorldRaw = { x: snapped.x, y: snapped.y };
      this.mouseWorld = { x: snapped.x, y: snapped.y };
      this.mouseScreen = { x: target.x, y: target.y };
      // Clear stale inactive class from checkmark
      const placeBtn = document.querySelector('.ring-center[data-dir="place"]');
      if (placeBtn) placeBtn.classList.remove('inactive');
      this._rectIntroAnimating = true;
      this._rectIntroAnimStart = performance.now();
      this._animateRectIntro();
      this._updateJoystickVisibility();
      this._startArrowPulse(3000);
      this._startCheckPulse(3000);
      this._updateFloatingCancel();
      this._updateFloatingRepeat();
      const label = this.objectType === 'circle' ? 'pillar' : 'column';
      this.updateStatus(`Move ${label} with arrows or tap to reposition, then tap \u2713 to place.`);
    }
    this.render();
  }

  _repeatDoorPlace(saved) {
    const wall = saved.wall;
    const room = saved.room;
    // If saved wall/room/param are missing, fall back to generic auto-place
    if (!wall || !room || saved.centerParam == null) {
      this._mobileDoorAutoPlace();
      return;
    }
    const wallLen = this.getWallLength(wall);
    const doorWidthFeet = this.doorWidth / 12;
    const halfParam = wallLen > 0 ? (doorWidthFeet / 2) / wallLen : 0;
    // Offset = door width + 1' gap, in param space
    const offsetParam = wallLen > 0 ? (doorWidthFeet + 1) / wallLen : 1;
    let placed = false;
    let newParam = null;
    // Try right (+param direction) first
    const rightParam = saved.centerParam + offsetParam;
    if (rightParam + halfParam <= 1.001 && rightParam - halfParam >= -0.001 &&
        !this._doorsOverlap(wall, room, rightParam - halfParam, rightParam + halfParam, null) &&
        !this._doorBlockedByConstraints(wall, rightParam - halfParam, rightParam + halfParam, doorWidthFeet)) {
      newParam = rightParam;
      placed = true;
    }
    // Try left (-param direction)
    if (!placed) {
      const leftParam = saved.centerParam - offsetParam;
      if (leftParam - halfParam >= -0.001 && leftParam + halfParam <= 1.001 &&
          !this._doorsOverlap(wall, room, leftParam - halfParam, leftParam + halfParam, null) &&
          !this._doorBlockedByConstraints(wall, leftParam - halfParam, leftParam + halfParam, doorWidthFeet)) {
        newParam = leftParam;
        placed = true;
      }
    }
    // Neither fits on same wall — fall back to generic auto-place
    if (!placed) {
      this._mobileDoorAutoPlace();
      return;
    }
    // Snap and place on the same wall at the offset position
    let snappedParam = this.snapParamToGrid(wall, newParam);
    snappedParam = Math.max(halfParam, Math.min(1 - halfParam, snappedParam));
    const snapPoint = this.paramToPoint(wall, snappedParam);
    const target = this._getMobileDotScreenPos();
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    this.panX = target.x - snapPoint.x * scale;
    this.panY = target.y - snapPoint.y * scale;
    this.mouseWorldRaw = { x: snapPoint.x, y: snapPoint.y };
    this.mouseWorld = { x: snapPoint.x, y: snapPoint.y };
    this._doorMobile = { phase: 1, wall, room, param: snappedParam, point: { x: snapPoint.x, y: snapPoint.y } };
    // Clear stale inactive class from checkmark
    const placeBtn = document.querySelector('.ring-center[data-dir="place"]');
    if (placeBtn) placeBtn.classList.remove('inactive');
    this._rectIntroAnimating = true;
    this._rectIntroAnimStart = performance.now();
    this._animateRectIntro();
    this._updateJoystickVisibility();
    this._setJoystickArrowsDirs(this._getDoorWallArrows(wall));
    this._startArrowPulse(3000);
    this._startCheckPulse(3000);
    this._updateFloatingCancel();
    this._updateFloatingRepeat();
    this.updateStatus('Slide door along wall, then tap \u2713.');
    this.render();
  }

  _updateToolbarEdgeEffect() {
    const el = this._toolbarEl;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewLeft = rect.left;
    const viewRight = rect.right;
    const edgeZone = 50; // px from edge where scaling kicks in
    const minScale = 0.78;

    const children = el.children;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (child.classList.contains('toolbar-divider') || child.classList.contains('dpad-spacer')) continue;
      const cr = child.getBoundingClientRect();
      const centerX = cr.left + cr.width / 2;

      // Distance from nearest edge
      const distFromLeft = centerX - viewLeft;
      const distFromRight = viewRight - centerX;
      const distFromEdge = Math.min(distFromLeft, distFromRight);

      let scale = 1;
      if (distFromEdge < edgeZone) {
        const t = Math.max(0, distFromEdge) / edgeZone; // 0 at edge, 1 at edgeZone
        scale = minScale + (1 - minScale) * t;
      }
      child.style.transform = scale < 0.99 ? `scale(${scale.toFixed(3)})` : '';
      child.style.opacity = scale < 0.99 ? (0.5 + 0.5 * scale).toFixed(3) : '';
    }
  }


  /* ── JOYSTICK D-PAD SETUP ── */

  _setupJoystick() {
    const dpad = document.getElementById('joystickDpad');
    if (!dpad) return;
    this._joystickEl = dpad;
    this._joystickHoldTimer = null;
    this._joystickHoldInterval = null;

    const buttons = dpad.querySelectorAll('[data-dir]');
    buttons.forEach(btn => {
      const dir = btn.dataset.dir;

      // Pointer down — start nudge + hold
      const onDown = (e) => {
        e.preventDefault();
        if (dir === 'place') {
          // Pen tool: confirm current wall point (only if joystick has stretched it)
          if (this.activeTool === 'pen' && this.mode === 'draw') {
            // Rectangle mode: confirm corners
            if (this.penDrawMode === 'rectangle') {
              // Corner 2 exists — finalize the rectangle
              if (this._rectCorner2 && this._rectCorner1) {
                const c1 = this._rectCorner1, c2 = this._rectCorner2;
                if (c1.x === c2.x || c1.y === c2.y) {
                  // Zero-size — hint user to stretch
                  this.updateStatus('Stretch the rectangle before confirming', { error: true });
                  if (navigator.vibrate) navigator.vibrate([20, 50, 20]);
                  this.render();
                  return;
                }
                // Lock-in animation at corner 2
                if (this._isMobileToolbar) this._animateRectLock({ x: c2.x, y: c2.y });
                this.saveUndoState();
                const minX = Math.min(c1.x, c2.x), maxX = Math.max(c1.x, c2.x);
                const minY = Math.min(c1.y, c2.y), maxY = Math.max(c1.y, c2.y);
                const tl = { x: minX, y: minY }, tr = { x: maxX, y: minY };
                const br = { x: maxX, y: maxY }, bl = { x: minX, y: maxY };
                const walls = [
                  { start: { x: tl.x, y: tl.y }, end: { x: tr.x, y: tr.y } },
                  { start: { x: tr.x, y: tr.y }, end: { x: br.x, y: br.y } },
                  { start: { x: br.x, y: br.y }, end: { x: bl.x, y: bl.y } },
                  { start: { x: bl.x, y: bl.y }, end: { x: tl.x, y: tl.y } }
                ];
                const room = { walls, doors: [], cutouts: [], type: this.roomType, closed: true };
                this.finalizeClosedRoom(room);
                this._lastRoomClosedAt = Date.now();
                this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
                this.currentWall = null; this.hasPlacedFirstPoint = false;
                if (navigator.vibrate) navigator.vibrate(50);
                this.updateMeasurements();
                this.updateStatus('Space created!');
                this._mobileLastEndWorld = { x: c2.x, y: c2.y };
                this._resetRectState();
                // Continuous drawing: auto-place next dot after short delay
                // Skip joystick visibility update on mobile — keeps joystick visible
                // during the gap so it doesn't flash hide then show
                if (this._isMobileToolbar) {
                  setTimeout(() => {
                    if (this.activeTool === 'pen' && this.penDrawMode === 'rectangle') {
                      this._mobileRectAutoPlace();
                    } else {
                      this._updateJoystickVisibility();
                    }
                  }, 500);
                } else {
                  this._updateJoystickVisibility();
                }
                this.render();
                return;
              }
              // Corner 1 not yet confirmed — lock it in, start corner 2 at same position
              if (this._rectCorner1 && !this._rectCorner1Confirmed) {
                this._rectCorner1Confirmed = true;
                // Clear pulse hints
                if (this._rectPulseTimer) { clearTimeout(this._rectPulseTimer); this._rectPulseTimer = null; }
                const penBtn = document.getElementById('penToolBtn');
                if (penBtn) penBtn.classList.remove('rect-pulse-hint', 'rect-pulse-subtract');
                this._clearCheckPulse();
                // Lock-in animation at corner 1
                if (this._isMobileToolbar) this._animateRectLock({ x: this._rectCorner1.x, y: this._rectCorner1.y });
                // Set corner 2 at corner 1's position — joystick now stretches corner 2
                this._rectCorner2 = { x: this._rectCorner1.x, y: this._rectCorner1.y };
                this.mouseWorld = { x: this._rectCorner2.x, y: this._rectCorner2.y };
                this._setJoystickArrowsEnabled(true);
                this._startArrowPulse(3000);
                // Checkmark inactive until rectangle has both width and height
                this._updateRectCheckmark();
                this._updateJoystickVisibility();
                this.updateStatus('Stretch the rectangle, then tap \u2713 to finish');
                if (navigator.vibrate) navigator.vibrate(30);
                this.render();
                return;
              }
              this.render();
              return;
            }
            // Freehand Phase 1: confirm starting point
            if (this._freehandAutoPlaced && !this._freehandStartConfirmed && this.currentWall) {
              this._freehandStartConfirmed = true;
              this._clearCheckPulse();
              this._clearArrowPulse();
              // Lock-in animation at start point
              if (this._isMobileToolbar) this._animateRectLock({ x: this.currentWall.start.x, y: this.currentWall.start.y });
              this._setJoystickArrowsEnabled(true);
              this._startArrowPulse(3000);
              // Checkmark grayed — wall is zero-length
              this._updateFreehandCheckmark();
              this.updateStatus('Stretch the wall, then tap \u2713 to confirm');
              if (navigator.vibrate) navigator.vibrate(30);
              this._updateJoystickVisibility();
              this.render();
              return;
            }
            // Freehand Phase 2: confirm wall point
            if (this.currentWall && this.hasPlacedFirstPoint) {
              // Only confirm if the wall has actual length (joystick was used)
              if (this.currentWall.start.x !== this.currentWall.end.x || this.currentWall.start.y !== this.currentWall.end.y) {
                const pt = { x: this.currentWall.end.x, y: this.currentWall.end.y };
                // Lock-in animation at confirmed point
                if (this._isMobileToolbar && this._freehandAutoPlaced) this._animateRectLock(pt);
                this.addWallPoint(pt);
                if (navigator.vibrate) navigator.vibrate(50);
                // Check if room closed (hasPlacedFirstPoint becomes false after closing)
                if (this._freehandAutoPlaced && !this.hasPlacedFirstPoint) {
                  // Room closed — continuous drawing: auto-place next dot after delay
                  this.updateStatus('Space created!');
                  this._mobileLastEndWorld = { x: pt.x, y: pt.y };
                  this._resetFreehandState();
                  // Skip joystick visibility update on mobile — keeps joystick visible
                  // during the gap so it doesn't flash hide then show
                  if (this._isMobileToolbar) {
                    setTimeout(() => {
                      if (this.activeTool === 'pen' && this.penDrawMode !== 'rectangle') {
                        this._mobileFreehandAutoPlace();
                      } else {
                        this._updateJoystickVisibility();
                      }
                    }, 500);
                  } else {
                    this._updateJoystickVisibility();
                  }
                  this.render();
                  return;
                }
                // Wall added but room not closed — update checkmark state for new zero-length wall
                if (this._freehandAutoPlaced) {
                  this._updateFreehandCheckmark();
                  this._clearArrowPulse();
                  this._startArrowPulse(3000);
                  this.updateStatus('Continue stretching walls. Close to first point to finish.');
                }
              }
            }
            this._updateJoystickVisibility();
            this.render();
            return;
          }
          // Mobile door tool: phase-aware checkmark confirms
          if (this.activeTool === 'door' && this._doorMobile) {
            const dm = this._doorMobile;
            if (dm.phase === 1) { this._doorMobileConfirmHinge(); return; }
            if (dm.phase === 2) { this._doorMobileConfirmEnd(); return; }
            if (dm.phase === 3) { this._doorMobileConfirmSwing(); return; }
            return;
          }
          // Door/objects tool: confirm placement at current position (desktop / rect+circle)
          if (this.activeTool === 'door' && this.mouseWorldRaw) {
            const sx = this.mouseScreen ? this.mouseScreen.x : 0;
            const sy = this.mouseScreen ? this.mouseScreen.y : 0;
            this._processDown(sx, sy, this.mouseWorldRaw, this.mouseWorld || this.mouseWorldRaw, 0, this.shiftHeld);
            this._processUp();
            if (navigator.vibrate) navigator.vibrate(50);
            this.render();
            return;
          }
          // Select tool: checkmark behavior depends on handle mode
          // doorSwingEdit: apply swing and finish
          if (this._selectHandleMode === 'doorSwingEdit') {
            const info = this._selectHandleInfo;
            const door = info.door;
            door.swing = info.swingIdx === -1 ? null : info.swingOption;
            // Return to cycling so user can continue with other handles
            const newCycleList = this._buildHandleCycleList();
            if (newCycleList.length > 0) {
              // Find the doorGrab entry to preserve cycle position
              const doorIdx = newCycleList.findIndex(item => item.mode === 'doorGrab' && item.door === door);
              this._selectHandleMode = 'cycling';
              this._selectHandleInfo = null;
              this._selectHandleCycleList = newCycleList;
              this._selectHandleCycleIndex = doorIdx >= 0 ? doorIdx : 0;
              this._setJoystickArrowsEnabled(true);
            } else {
              this._selectHandleMode = null;
              this._selectHandleInfo = null;
              this._selectHandleCycleList = [];
              this._selectHandleCycleIndex = 0;
              this._evaluateSelectJoystickMode();
            }
            this._clearArrowPulse();
            this._clearCheckPulse();
            this._startArrowPulse(3000);
            this._startCheckPulse(3000);
            this._updateJoystickVisibility();
            if (navigator.vibrate) navigator.vibrate(50);
            this.updateStatus('Door swing updated!');
            this.render(); this.updateMeasurements();
            return;
          }
          // doorGrab: lock position, enter swing edit
          if (this._selectHandleMode === 'doorGrab') {
            const info = this._selectHandleInfo;
            const door = info.door;
            if (door.type === 'roller') {
              this._deactivateSelectHandle();
              this.selectedDoors = new Set();
              this._updateSelectionStatus();
              this._updateJoystickVisibility();
              if (navigator.vibrate) navigator.vibrate(50);
              this.updateStatus('Door position locked.');
              this.render();
              return;
            }
            const room = this._findRoomForWall(door.wall);
            const swingOptions = this._getSwingOptions(door.type);
            let swingIdx = -1, swingOption = null;
            if (door.swing) {
              for (let i = 0; i < swingOptions.length; i++) {
                const opt = swingOptions[i];
                if (opt.perpSign === door.swing.perpSign &&
                    (opt.hingeEnd || null) === (door.swing.hingeEnd || null)) {
                  swingIdx = i; swingOption = opt; break;
                }
              }
            }
            this.saveUndoState();
            this._selectHandleMode = 'doorSwingEdit';
            this._selectHandleInfo = { door, room, swingOptions, swingIdx, swingOption };
            this._updateDoorEditSwingArrows();
            this._clearArrowPulse();
            this._clearCheckPulse();
            this._startArrowPulse(1500);
            this._startCheckPulse(3000);
            if (navigator.vibrate) navigator.vibrate(50);
            this.updateStatus('Use arrows to set swing direction, then \u2713.');
            this.render();
            return;
          }
          if (this._selectHandleMode === 'cycling') {
            // Cycling mode — activate the currently highlighted handle
            const item = this._selectHandleCycleList[this._selectHandleCycleIndex];
            if (item) this._activateSelectHandleFromCycle(item);
            this.render();
            return;
          }
          if (this._selectHandleMode && this._selectHandleMode !== 'wholeMove') {
            // Specific handle active — return to cycling mode, preserve index
            const savedIndex = this._selectHandleCycleIndex;
            if (this._selectRotateDialActive) this._exitRotateDial();
            const newCycleList = this._buildHandleCycleList();
            if (newCycleList.length > 1 || (newCycleList.length === 1 && newCycleList[0].mode !== 'wholeMove')) {
              this._selectHandleMode = 'cycling';
              this._selectHandleInfo = null;
              this._selectHandleCycleList = newCycleList;
              this._selectHandleCycleIndex = Math.min(savedIndex, newCycleList.length - 1);
              this._setJoystickArrowsEnabled(true);
            } else {
              this._selectHandleMode = null;
              this._selectHandleInfo = null;
              this._selectHandleCycleList = [];
              this._selectHandleCycleIndex = 0;
              this._evaluateSelectJoystickMode();
            }
            this._clearArrowPulse();
            this._clearCheckPulse();
            this._startArrowPulse(3000);
            this._startCheckPulse(3000);
            if (navigator.vibrate) navigator.vibrate(15);
            this.render();
            return;
          }
          if (this._selectHandleMode === 'wholeMove') {
            // Single door: skip cycling, go straight to doorGrab
            if (this.selectedDoors.size === 1 && this.selectedWalls.size === 0 && this.selectedObjects.size === 0) {
              const door = this.selectedDoors.values().next().value;
              this._selectHandleMode = 'doorGrab';
              this._selectHandleInfo = { door };
              this._setJoystickArrowsDirs(this._getDoorWallArrows(door.wall));
              this._clearArrowPulse();
              this._clearCheckPulse();
              this._startArrowPulse(3000);
              this._startCheckPulse(3000);
              if (navigator.vibrate) navigator.vibrate(15);
              this.render();
              return;
            }
            // wholeMove — check if individual handles exist to cycle through
            const wmCycleList = this._buildHandleCycleList();
            if (wmCycleList.length > 0) {
              this._selectHandleMode = 'cycling';
              this._selectHandleInfo = null;
              this._selectHandleCycleList = wmCycleList;
              this._selectHandleCycleIndex = 0;
              this._setJoystickArrowsEnabled(true);
              this._clearArrowPulse();
              this._clearCheckPulse();
              this._startArrowPulse(3000);
              this._startCheckPulse(3000);
              if (navigator.vibrate) navigator.vibrate(15);
              this.render();
              return;
            }
          }
          // No handles or no mode — deselect all
          this._deactivateSelectHandle();
          this.selectedWalls = new Set();
          this.selectedDoors = new Set();
          this.selectedObjects = new Set();
          this.selectionStart = null;
          this.selectionEnd = null;
          this._updateSelectionStatus();
          this.render();
          return;
        }
        // Cycling mode: arrows cycle through handle list
        if (this.activeTool === 'select' && this._selectHandleMode === 'cycling') {
          const len = this._selectHandleCycleList.length;
          if (len > 0) {
            if (dir === 'right' || dir === 'down') {
              this._selectHandleCycleIndex = (this._selectHandleCycleIndex + 1) % len;
            } else {
              this._selectHandleCycleIndex = (this._selectHandleCycleIndex - 1 + len) % len;
            }
            const cycleItem = this._selectHandleCycleList[this._selectHandleCycleIndex];
            const cyclePos = cycleItem ? this._getCycleItemWorldPos(cycleItem) : null;
            if (cyclePos) this._mobilePanToWorldPoint(cyclePos.x, cyclePos.y);
            if (navigator.vibrate) navigator.vibrate(15);
            this._restartJoystickPulse();
            this.render();
          }
          // Hold-repeat for cycling: 300ms initial, 200ms interval, no acceleration
          this._joystickHoldTimer = setTimeout(() => {
            const cycleTick = () => {
              if (this._selectHandleMode !== 'cycling') return;
              const l = this._selectHandleCycleList.length;
              if (l === 0) return;
              if (dir === 'right' || dir === 'down') {
                this._selectHandleCycleIndex = (this._selectHandleCycleIndex + 1) % l;
              } else {
                this._selectHandleCycleIndex = (this._selectHandleCycleIndex - 1 + l) % l;
              }
              const ci = this._selectHandleCycleList[this._selectHandleCycleIndex];
              const cp = ci ? this._getCycleItemWorldPos(ci) : null;
              if (cp) this._mobilePanToWorldPoint(cp.x, cp.y);
              if (navigator.vibrate) navigator.vibrate(15);
              this._restartJoystickPulse();
              this.render();
              this._joystickHoldInterval = setTimeout(cycleTick, 200);
            };
            this._joystickHoldInterval = setTimeout(cycleTick, 200);
          }, 300);
          return;
        }
        // doorSwingEdit: arrows cycle swing options
        if (this.activeTool === 'select' && this._selectHandleMode === 'doorSwingEdit') {
          this._cycleDoorEditSwing(dir);
          this._updateDoorEditSwingArrows();
          if (navigator.vibrate) navigator.vibrate(15);
          this._restartJoystickPulse();
          this.render();
          this._joystickHoldTimer = setTimeout(() => {
            const swingTick = () => {
              if (this._selectHandleMode !== 'doorSwingEdit') return;
              this._cycleDoorEditSwing(dir);
              this._updateDoorEditSwingArrows();
              if (navigator.vibrate) navigator.vibrate(15);
              this._restartJoystickPulse();
              this.render();
              this._joystickHoldInterval = setTimeout(swingTick, 200);
            };
            this._joystickHoldInterval = setTimeout(swingTick, 200);
          }, 300);
          return;
        }
        // wholeMove + single door: block arrows until doorGrab is activated via checkmark
        if (this.activeTool === 'select' && this._selectHandleMode === 'wholeMove' &&
            this.selectedDoors.size === 1 && this.selectedWalls.size === 0 && this.selectedObjects.size === 0) {
          return;
        }
        // doorGrab: only slide the door along its wall (ignore other selected items)
        if (this.activeTool === 'select' && this._selectHandleMode === 'doorGrab') {
          const door = this._selectHandleInfo.door;
          if (!this._nudgeUndoSaved) { this.saveUndoState(); this._nudgeUndoSaved = true; }
          this._selectedDoorTravelDir = this._arrowToWallDelta(dir, door.wall);
          this._slideSelectedDoorAlongWall(this._selectedDoorTravelDir);
          this._restartJoystickPulse();
          this.render();
          this._joystickHoldTimer = setTimeout(() => {
            let interval = 150;
            let count = 0;
            const tick = () => {
              if (this._selectHandleMode !== 'doorGrab') return;
              const moved = this._slideSelectedDoorAlongWall(this._selectedDoorTravelDir);
              this._restartJoystickPulse();
              if (!moved) return;
              count++;
              if (count > 8 && interval > 100) interval = 100;
              this._joystickHoldInterval = setTimeout(tick, interval);
            };
            this._joystickHoldInterval = setTimeout(tick, interval);
          }, 400);
          return;
        }
        // Only save undo for selection-mode nudging (moving placed objects).
        // Draw modes (pen/door) save undo at the right moments inside
        // addWallPoint() / checkmark handlers — arrows just move previews.
        // Handle mode nudging saves undo at activation time, not here.
        if (this.activeTool === 'select' && !this._nudgeUndoSaved && !this._selectHandleMode) {
          this.saveUndoState();
          this._nudgeUndoSaved = true;
        }
        // Route through handle nudge or standard nudge
        // Single selected door always uses _nudgeSelection (has wall-following + snaking)
        const isSingleDoorSelect = this.activeTool === 'select' &&
          this.selectedDoors.size === 1 && this.selectedWalls.size === 0 && this.selectedObjects.size === 0;
        if (this.activeTool === 'select' && this._selectHandleMode && !isSingleDoorSelect) {
          this._nudgeSelectHandle(dir);
        } else {
          this._nudgeSelection(dir);
        }
        // Door phase 1: wall-following hold with travel direction tracking
        if (this.activeTool === 'door' && this._doorMobile && this._doorMobile.phase === 1) {
          this._joystickHoldTimer = setTimeout(() => {
            let interval = 150;
            let count = 0;
            const tick = () => {
              if (!this._doorMobile || this._doorMobile.phase !== 1) return;
              this._moveDoorAlongWall(this._doorMobile.travelDir);
              this._restartJoystickPulse();
              count++;
              // Gentle speedup, capped at 100ms (never gets rapid)
              if (count > 8 && interval > 100) interval = 100;
              this._joystickHoldInterval = setTimeout(tick, interval);
            };
            this._joystickHoldInterval = setTimeout(tick, interval);
          }, 400);
          return;
        }
        // Selected door: wall-following hold with corner snaking
        if (isSingleDoorSelect) {
          const singleDoor = this.selectedDoors.values().next().value;
          this._selectedDoorTravelDir = this._arrowToWallDelta(dir, singleDoor.wall);
          this._joystickHoldTimer = setTimeout(() => {
            let interval = 150;
            let count = 0;
            const tick = () => {
              if (this.selectedDoors.size !== 1) return;
              const moved = this._slideSelectedDoorAlongWall(this._selectedDoorTravelDir);
              this._restartJoystickPulse();
              if (!moved) return; // dead end — stop
              count++;
              if (count > 8 && interval > 100) interval = 100;
              this._joystickHoldInterval = setTimeout(tick, interval);
            };
            this._joystickHoldInterval = setTimeout(tick, interval);
          }, 400);
          return;
        }
        // Start hold: after 500ms, begin continuous nudging
        const nudgeFn = (this.activeTool === 'select' && this._selectHandleMode)
          ? () => this._nudgeSelectHandle(dir) : () => this._nudgeSelection(dir);
        this._joystickHoldTimer = setTimeout(() => {
          let interval = 150;
          let count = 0;
          const tick = () => {
            nudgeFn();
            count++;
            // Accelerate after 5 nudges
            if (count > 5 && interval > 50) interval = 80;
            if (count > 15 && interval > 30) interval = 40;
            this._joystickHoldInterval = setTimeout(tick, interval);
          };
          this._joystickHoldInterval = setTimeout(tick, interval);
        }, 500);
      };

      const onUp = (e) => {
        e.preventDefault();
        clearTimeout(this._joystickHoldTimer);
        clearTimeout(this._joystickHoldInterval);
        this._joystickHoldTimer = null;
        this._joystickHoldInterval = null;
        this._nudgeUndoSaved = false;
        // After hold ends, refresh arrows for the door's current wall
        if (this.activeTool === 'select' && this._selectHandleMode === 'doorSwingEdit') {
          this._updateDoorEditSwingArrows();
        } else if (this.activeTool === 'select' && this._selectHandleMode === 'doorGrab' && this._selectHandleInfo) {
          this._setJoystickArrowsDirs(this._getDoorWallArrows(this._selectHandleInfo.door.wall));
        } else if (this.activeTool === 'select' && this.selectedDoors.size === 1 &&
            this.selectedWalls.size === 0 && this.selectedObjects.size === 0) {
          const door = this.selectedDoors.values().next().value;
          this._setJoystickArrowsDirs(this._getDoorWallArrows(door.wall));
        }
      };

      btn.addEventListener('pointerdown', onDown);
      btn.addEventListener('pointerup', onUp);
      btn.addEventListener('pointercancel', onUp);
      btn.addEventListener('pointerleave', onUp);
    });
  }

  _resetDoorMobileState() {
    this._doorMobile = null;
    this._doorMobilePrompt = false;
    this.doorPlacement = null;
    this._rectIntroAnimating = false;
    this._clearCheckPulse();
    this._clearArrowPulse();
    // Kill any in-progress joystick hold-repeat to prevent stale ticks after tool/object switch
    clearTimeout(this._joystickHoldTimer);
    clearTimeout(this._joystickHoldInterval);
    this._joystickHoldTimer = null;
    this._joystickHoldInterval = null;
    this._setJoystickArrowsEnabled(true);
    this._setJoystickArrowsDirs({ up: true, down: true, left: true, right: true });
    const placeBtn = document.querySelector('.ring-center[data-dir="place"]');
    if (placeBtn) placeBtn.classList.remove('inactive');
    this._updateJoystickVisibility();
  }

  _startCheckPulse(delay = 3000) {
    this._clearCheckPulse();
    this._checkPulseTimer = setTimeout(() => {
      const placeBtn = document.querySelector('.ring-center[data-dir="place"]');
      if (placeBtn) { placeBtn.classList.add('rect-pulse-hint'); placeBtn.classList.toggle('rect-pulse-subtract', this.roomType === 'subtract'); }
    }, delay);
  }

  _clearCheckPulse() {
    if (this._checkPulseTimer) { clearTimeout(this._checkPulseTimer); this._checkPulseTimer = null; }
    const placeBtn = document.querySelector('.ring-center[data-dir="place"]');
    if (placeBtn) placeBtn.classList.remove('rect-pulse-hint', 'rect-pulse-subtract');
  }

  _startArrowPulse(delay = 3000) {
    this._clearArrowPulse();
    this._arrowPulseTimer = setTimeout(() => {
      const dpad = document.getElementById('joystickDpad');
      if (!dpad) return;
      const isSub = this.roomType === 'subtract';
      dpad.querySelectorAll('.ring-seg').forEach(btn => { if (!btn.classList.contains('inactive') && !btn.classList.contains('disabled')) { btn.classList.add('rect-pulse-hint'); btn.classList.toggle('rect-pulse-subtract', isSub); } });
      // Also pulse checkmark if it's active (not inactive/grayed out)
      const placeBtn = dpad.querySelector('.ring-center[data-dir="place"]');
      if (placeBtn && !placeBtn.classList.contains('inactive')) {
        placeBtn.classList.add('rect-pulse-hint'); placeBtn.classList.toggle('rect-pulse-subtract', isSub);
      }
    }, delay);
  }

  _clearArrowPulse() {
    if (this._arrowPulseTimer) { clearTimeout(this._arrowPulseTimer); this._arrowPulseTimer = null; }
    const dpad = document.getElementById('joystickDpad');
    if (dpad) {
      dpad.querySelectorAll('.ring-seg').forEach(btn => btn.classList.remove('rect-pulse-hint', 'rect-pulse-subtract'));
      const placeBtn = dpad.querySelector('.ring-center[data-dir="place"]');
      if (placeBtn) placeBtn.classList.remove('rect-pulse-hint', 'rect-pulse-subtract');
    }
  }

  // Restart the appropriate pulse based on current draw state
  _restartJoystickPulse() {
    // Select handle mode pulse logic
    if (this.activeTool === 'select' && this._selectHandleMode) {
      this._clearCheckPulse();
      this._clearArrowPulse();
      this._startArrowPulse(3000);
      this._startCheckPulse(3000);
      return;
    }
    // Door mobile pulse logic
    if (this._doorMobile) {
      const dm = this._doorMobile;
      if (dm.phase === 1) {
        this._clearCheckPulse();
        this._startArrowPulse(3000);
        this._startCheckPulse(3000);
      } else if (dm.phase === 2) {
        this._clearCheckPulse();
        this._startArrowPulse(3000);
      } else if (dm.phase === 3) {
        this._clearCheckPulse();
        this._startArrowPulse(1500);
        this._startCheckPulse(3000);
      }
      return;
    }
    // Freehand pulse logic
    if (this.activeTool === 'pen' && this._freehandAutoPlaced) {
      if (!this._freehandStartConfirmed) {
        // Phase 1: pulse checkmark (confirm start)
        this._clearArrowPulse();
        this._startCheckPulse(3000);
      } else if (this.currentWall && this.currentWall.start.x === this.currentWall.end.x && this.currentWall.start.y === this.currentWall.end.y) {
        // Phase 2, zero-length wall: pulse arrows (need to stretch)
        this._clearCheckPulse();
        this._startArrowPulse(3000);
      } else {
        // Phase 2, wall has length: pulse arrows + checkmark
        this._clearCheckPulse();
        this._startArrowPulse(3000);
      }
      return;
    }
    if (this.activeTool !== 'pen' || this.penDrawMode !== 'rectangle') {
      this._clearCheckPulse();
      this._clearArrowPulse();
      return;
    }
    // Corner 2 active — pulse arrows (need to stretch)
    if (this._rectCorner2) {
      this._clearCheckPulse();
      this._startArrowPulse(3000);
    // Corner 1 active, not confirmed — pulse checkmark (need to confirm)
    } else if (this._rectCorner1 && !this._rectCorner1Confirmed) {
      this._clearArrowPulse();
      this._startCheckPulse(3000);
    } else {
      this._clearCheckPulse();
      this._clearArrowPulse();
    }
  }

  _setJoystickArrowsEnabled(enabled) {
    const dpad = document.getElementById('joystickDpad');
    if (!dpad) return;
    dpad.querySelectorAll('.ring-seg').forEach(btn => {
      btn.classList.toggle('disabled', !enabled);
    });
  }

  _setJoystickArrowsDirs(dirs) {
    // dirs = { up: bool, down: bool, left: bool, right: bool }
    const dpad = document.getElementById('joystickDpad');
    if (!dpad) return;
    dpad.querySelectorAll('.ring-seg').forEach(btn => {
      const d = btn.getAttribute('data-dir');
      if (dirs.hasOwnProperty(d)) btn.classList.toggle('disabled', !dirs[d]);
    });
  }

  _getDoorWallArrows(wall) {
    // Returns {up,down,left,right} bools — enables the 2 arrows most aligned with the wall direction
    const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
    if (wLen === 0) return { up: false, down: false, left: false, right: false };
    const dirX = wdx / wLen, dirY = wdy / wLen;
    // Project each cardinal direction onto wall direction
    const projs = [
      { dir: 'up', val: Math.abs(dirY * -1) },     // up = (0, -1)
      { dir: 'down', val: Math.abs(dirY * 1) },     // down = (0, 1)
      { dir: 'left', val: Math.abs(dirX * -1) },    // left = (-1, 0)
      { dir: 'right', val: Math.abs(dirX * 1) }     // right = (1, 0)
    ];
    projs.sort((a, b) => b.val - a.val);
    const result = { up: false, down: false, left: false, right: false };
    result[projs[0].dir] = true;
    result[projs[1].dir] = true;
    return result;
  }

  _arrowToWallDelta(dir, wall) {
    // Converts a cardinal arrow direction to +1 (toward wall.end) or -1 (toward wall.start)
    const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
    if (wLen === 0) return 0;
    const dirX = wdx / wLen, dirY = wdy / wLen;
    let arrowX = 0, arrowY = 0;
    if (dir === 'up') arrowY = -1;
    else if (dir === 'down') arrowY = 1;
    else if (dir === 'left') arrowX = -1;
    else if (dir === 'right') arrowX = 1;
    const dot = arrowX * dirX + arrowY * dirY;
    return dot >= 0 ? 1 : -1;
  }


  /* ── SELECT TOOL MOBILE JOYSTICK ── */

  _isEntireRoomSelected(room) {
    if (!room || !room.walls || room.walls.length === 0) return false;
    for (const wall of room.walls) {
      if (!this.selectedWalls.has(wall)) return false;
    }
    return true;
  }

  _shouldAutoJoystick() {
    const hasWalls = this.selectedWalls.size > 0;
    const hasDoors = this.selectedDoors.size > 0;
    const hasObjects = this.selectedObjects.size > 0;
    if (!hasWalls && !hasDoors && !hasObjects) return null;
    // No walls → wholeMove (objects/doors only)
    if (!hasWalls) return 'wholeMove';
    // Walls selected — check if each room with a selected wall has ALL its walls selected
    const roomsChecked = new Set();
    for (const wall of this.selectedWalls) {
      const room = this._findRoomForWall(wall);
      if (!room || roomsChecked.has(room)) continue;
      roomsChecked.add(room);
      if (!this._isEntireRoomSelected(room)) return 'deferred';
    }
    return 'wholeMove';
  }

  _getWallGrabPerpArrows(wall) {
    // Returns {up,down,left,right} bools — enables the 2 arrows most aligned with the wall's perpendicular
    const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
    if (wLen === 0) return { up: false, down: false, left: false, right: false };
    // Perpendicular direction: (-wdy, wdx) normalized
    const perpX = -wdy / wLen, perpY = wdx / wLen;
    const projs = [
      { dir: 'up', val: Math.abs(perpY * -1 + perpX * 0) },     // up = (0, -1)
      { dir: 'down', val: Math.abs(perpY * 1 + perpX * 0) },     // down = (0, 1)
      { dir: 'left', val: Math.abs(perpX * -1 + perpY * 0) },    // left = (-1, 0)
      { dir: 'right', val: Math.abs(perpX * 1 + perpY * 0) }     // right = (1, 0)
    ];
    projs.sort((a, b) => b.val - a.val);
    const result = { up: false, down: false, left: false, right: false };
    result[projs[0].dir] = true;
    result[projs[1].dir] = true;
    return result;
  }

  _getResizeArrows(handleName) {
    // Returns arrow config based on resize handle position
    const corners = ['nw', 'ne', 'sw', 'se'];
    if (corners.includes(handleName)) return { up: true, down: true, left: true, right: true };
    if (handleName === 'n' || handleName === 's') return { up: true, down: true, left: false, right: false };
    if (handleName === 'e' || handleName === 'w') return { up: false, down: false, left: true, right: true };
    return { up: true, down: true, left: true, right: true };
  }

  _buildHandleCycleList() {
    const list = [];
    // Note: wholeMove is NOT included in the cycle list — it's entered directly
    // by _evaluateSelectJoystickMode. Checkmark in wholeMove transitions to cycling.
    // 1. Wall handles (per selected wall)
    const pkey = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
    const addedCorners = new Set();
    for (const wall of this.selectedWalls) {
      const room = this._findRoomForWall(wall);
      // Use visible segment check: wall is hidden if it has no visible segments
      const hasVisibleSegments = room ? this._getHandleSegmentsForWall(wall, room).length > 0 : true;
      // Open endpoint dots (only for rooms without cutouts — cutout rooms use visible segment corners)
      if (room && hasVisibleSegments && (!room.cutouts || room.cutouts.length === 0)) {
        for (const whichEnd of this._getWallOpenEndpoints(wall, room)) {
          list.push({ mode: 'wallEndpoint', wall, room, whichEnd });
        }
      }
      // Corner handles (deduplicated by position)
      if (room && hasVisibleSegments) {
        for (const h of this._getWallCornerHandles(wall, room)) {
          const k = pkey({ x: h.wx, y: h.wy });
          if (addedCorners.has(k)) continue;
          addedCorners.add(k);
          list.push({ mode: 'wallCorner', wall, room, whichEnd: h.whichEnd });
        }
      }
      // Grab handle (use visible segment check instead of blanket cutout skip)
      if (hasVisibleSegments) {
        list.push({ mode: 'wallGrab', wall });
      }
    }
    // 2b. Door grab handles
    for (const door of this.selectedDoors) {
      list.push({ mode: 'doorGrab', door });
    }
    // 3. Object handles (skip doors)
    for (const obj of this.selectedObjects) {
      if (obj.type === 'door') continue;
      // Center move handle (mobile only) — first in cycle order
      if (this._isMobile) {
        list.push({ mode: 'objectMove', obj });
      }
      if (obj.type === 'circle') {
        // Individual resize handles for circles (n, e, s, w)
        for (const hName of ['n', 'e', 's', 'w']) {
          list.push({ mode: 'resize', obj, handle: hName });
        }
      } else if (obj.type === 'equipment') {
        // Equipment: rotate only, no resize (to-scale from DWG)
        list.push({ mode: 'rotate', obj });
      } else if (obj.type === 'rectangle') {
        // On mobile: 4 corner handles only; desktop: all 8
        const rectHandles = this._isMobile
          ? ['nw', 'ne', 'se', 'sw']
          : ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
        for (const hName of rectHandles) {
          list.push({ mode: 'resize', obj, handle: hName });
        }
        // Rotate handle
        list.push({ mode: 'rotate', obj });
      }
    }
    // Sort by reading order: top-to-bottom, left-to-right
    const getHandlePos = (item) => {
      if (item.mode === 'wallEndpoint') {
        const pt = item.whichEnd === 'start' ? item.wall.start : item.wall.end;
        return { x: pt.x, y: pt.y };
      }
      if (item.mode === 'wallCorner') {
        const handles = this._getWallCornerHandles(item.wall, item.room);
        const h = handles.find(h => h.whichEnd === item.whichEnd);
        return h ? { x: h.wx, y: h.wy } : null;
      }
      if (item.mode === 'wallGrab') {
        const room = this._findRoomForWall(item.wall);
        const mid = this._getWallGrabHandle(item.wall, room);
        return mid ? { x: mid.wx, y: mid.wy } : null;
      }
      if (item.mode === 'doorGrab') {
        const midParam = (item.door.hingeParam + item.door.endParam) / 2;
        const midPt = this.paramToPoint(item.door.wall, midParam);
        return { x: midPt.x, y: midPt.y };
      }
      if (item.mode === 'objectMove') {
        return { x: item.obj.x, y: item.obj.y };
      }
      if (item.mode === 'resize') {
        const handles = this._getResizeHandles(item.obj);
        const h = handles.find(h => h.name === item.handle);
        return h ? { x: h.wx, y: h.wy } : null;
      }
      if (item.mode === 'rotate') {
        const rotH = this._getRotateHandle(item.obj);
        return rotH ? { x: rotH.wx, y: rotH.wy } : null;
      }
      return null;
    };
    const positioned = list.map(item => ({ item, pos: getHandlePos(item) }));
    positioned.sort((a, b) => {
      if (!a.pos && !b.pos) return 0;
      if (!a.pos) return 1;
      if (!b.pos) return -1;
      const dy = a.pos.y - b.pos.y;
      if (Math.abs(dy) > 0.1) return dy;
      return a.pos.x - b.pos.x;
    });
    return positioned.map(p => p.item);
  }

  _isCycleHighlighted(mode, matchProps) {
    if (this._selectHandleMode !== 'cycling') return false;
    const item = this._selectHandleCycleList[this._selectHandleCycleIndex];
    if (!item || item.mode !== mode) return false;
    if (matchProps.wall !== undefined && item.wall !== matchProps.wall) return false;
    if (matchProps.whichEnd !== undefined && item.whichEnd !== matchProps.whichEnd) return false;
    if (matchProps.obj !== undefined && item.obj !== matchProps.obj) return false;
    if (matchProps.door !== undefined && item.door !== matchProps.door) return false;
    if (matchProps.handle !== undefined) {
      if (item.handle !== matchProps.handle) return false;
    }
    return true;
  }

  _evaluateSelectJoystickMode() {
    if (!this._isMobileToolbar) return;
    if (this.activeTool !== 'select') return;
    const autoMode = this._shouldAutoJoystick();
    const cycleList = this._buildHandleCycleList();

    if (autoMode === 'wholeMove') {
      // Enter wholeMove directly — arrows move the selection
      // Checkmark will transition to individual handle cycling if handles exist
      this._selectHandleMode = 'wholeMove';
      if (this.selectedDoors.size === 1 && this.selectedWalls.size === 0 && this.selectedObjects.size === 0) {
        const singleDoor = this.selectedDoors.values().next().value;
        this._selectHandleInfo = { singleDoor };
        // Arrows disabled in wholeMove — press checkmark first to activate doorGrab
        this._setJoystickArrowsDirs({ up: false, down: false, left: false, right: false });
      } else {
        this._selectHandleInfo = null;
        this._setJoystickArrowsEnabled(true);
      }
      this._selectHandleCycleList = cycleList;
      this._selectHandleCycleIndex = 0;
      this._updateJoystickVisibility();
      this._clearArrowPulse();
      this._clearCheckPulse();
      this._startArrowPulse(3000);
      this._startCheckPulse(3000);
    } else if (cycleList.length > 0) {
      // Handles available but no wholeMove — enter cycling directly
      this._selectHandleMode = 'cycling';
      this._selectHandleInfo = null;
      this._selectHandleCycleList = cycleList;
      this._selectHandleCycleIndex = 0;
      this._setJoystickArrowsEnabled(true);
      this._updateJoystickVisibility();
      this._clearArrowPulse();
      this._clearCheckPulse();
      this._startArrowPulse(3000);
      this._startCheckPulse(3000);
    } else {
      // No selection or no handles
      this._selectHandleMode = null;
      this._selectHandleInfo = null;
      this._selectHandleCycleList = [];
      this._selectHandleCycleIndex = 0;
      this._updateJoystickVisibility();
      this._clearArrowPulse();
      this._clearCheckPulse();
    }
  }

  _activateSelectHandleFromCycle(item) {
    if (item.mode === 'wholeMove') {
      // Enter wholeMove directly
      this.saveUndoState();
      this._selectHandleMode = 'wholeMove';
      if (item.singleDoor) {
        this._selectHandleInfo = { singleDoor: item.singleDoor };
        this._setJoystickArrowsDirs(this._getDoorWallArrows(item.singleDoor.wall));
      } else {
        this._selectHandleInfo = null;
        this._setJoystickArrowsEnabled(true);
      }
      this._updateJoystickVisibility();
      this._clearArrowPulse();
      this._clearCheckPulse();
      this._startArrowPulse(3000);
      this._startCheckPulse(3000);
      if (navigator.vibrate) navigator.vibrate(30);
      // Pan to centroid of selection on mobile
      if (this._isMobile) {
        let sumX = 0, sumY = 0, count = 0;
        for (const w of this.selectedWalls) { sumX += (w.start.x + w.end.x) / 2; sumY += (w.start.y + w.end.y) / 2; count++; }
        for (const obj of this.selectedObjects) { sumX += obj.x; sumY += obj.y; count++; }
        for (const door of this.selectedDoors) {
          const mp = (door.hingeParam + door.endParam) / 2;
          const pt = this.paramToPoint(door.wall, mp);
          sumX += pt.x; sumY += pt.y; count++;
        }
        if (count > 0) this._mobilePanToWorldPoint(sumX / count, sumY / count);
      }
      this.render();
      return;
    }
    if (item.mode === 'resize') {
      const obj = item.obj;
      const oppName = this._getOppositeHandle(item.handle);
      const handles = this._getResizeHandles(obj);
      const oppH = handles.find(h => h.name === oppName);
      this._activateSelectHandle('resize', {
        obj, handle: item.handle,
        anchorWx: oppH ? oppH.wx : obj.x, anchorWy: oppH ? oppH.wy : obj.y,
        origX: obj.x, origY: obj.y,
        origWidth: obj.width, origDepth: obj.depth,
        origDiameter: obj.diameter, origRotation: obj.rotation || 0
      });
      return;
    }
    if (item.mode === 'rotate') {
      this._activateSelectHandle('rotate', { obj: item.obj, origRotation: item.obj.rotation || 0 });
      return;
    }
    if (item.mode === 'wallEndpoint') {
      const pt = item.whichEnd === 'start' ? item.wall.start : item.wall.end;
      this._activateSelectHandle('wallEndpoint', {
        wall: item.wall, room: item.room, whichEnd: item.whichEnd,
        origX: pt.x, origY: pt.y
      });
      return;
    }
    if (item.mode === 'wallCorner') {
      this._activateSelectHandle('wallCorner', {
        wall: item.wall, room: item.room, whichEnd: item.whichEnd
      });
      return;
    }
    if (item.mode === 'wallGrab') {
      this._activateSelectHandle('wallGrab', { wall: item.wall });
      return;
    }
    if (item.mode === 'objectMove') {
      this.saveUndoState();
      this._selectHandleMode = 'objectMove';
      this._selectHandleInfo = { obj: item.obj };
      this._setJoystickArrowsEnabled(true);
      this._updateJoystickVisibility();
      this._clearArrowPulse();
      this._clearCheckPulse();
      this._startArrowPulse(3000);
      this._startCheckPulse(3000);
      if (navigator.vibrate) navigator.vibrate(30);
      if (this._isMobile) this._mobilePanToWorldPoint(item.obj.x, item.obj.y);
      this.render();
      return;
    }
    if (item.mode === 'doorGrab') {
      // Activate doorGrab — solid green handle, arrows only move this door
      this.saveUndoState();
      this._selectHandleMode = 'doorGrab';
      this._selectHandleInfo = { door: item.door };
      this._setJoystickArrowsDirs(this._getDoorWallArrows(item.door.wall));
      this._updateJoystickVisibility();
      this._clearArrowPulse();
      this._clearCheckPulse();
      this._startArrowPulse(3000);
      this._startCheckPulse(3000);
      if (navigator.vibrate) navigator.vibrate(30);
      // Pan to door midpoint on mobile
      if (this._isMobile) {
        const midParam = (item.door.hingeParam + item.door.endParam) / 2;
        const midPt = this.paramToPoint(item.door.wall, midParam);
        this._mobilePanToWorldPoint(midPt.x, midPt.y);
      }
      this.render();
      return;
    }
  }

  _activateSelectHandle(mode, info) {
    this.saveUndoState();
    this._selectHandleMode = mode;
    // Build full context for each mode
    if (mode === 'wallGrab') {
      const wall = info.wall;
      const room = this._findRoomForWall(wall);
      const connStart = room ? this._getWallsConnectedAt(wall, room, 'start') : [];
      const connEnd = room ? this._getWallsConnectedAt(wall, room, 'end') : [];
      const connStartOrig = connStart.map(w => ({ wall: w, startX: w.start.x, startY: w.start.y, endX: w.end.x, endY: w.end.y }));
      const connEndOrig = connEnd.map(w => ({ wall: w, startX: w.start.x, startY: w.start.y, endX: w.end.x, endY: w.end.y }));
      const doorSnapshots = [];
      if (room) {
        for (const cw of [...connStart, ...connEnd, wall]) {
          for (const door of room.doors) {
            if (door.wall === cw) {
              doorSnapshots.push({ door, hingePos: this.paramToPoint(cw, door.hingeParam), endPos: this.paramToPoint(cw, door.endParam) });
            }
          }
        }
      }
      this._selectHandleInfo = {
        wall, room, connStart, connEnd, connStartOrig, connEndOrig,
        origStartX: wall.start.x, origStartY: wall.start.y,
        origEndX: wall.end.x, origEndY: wall.end.y,
        doorSnapshots, cumDx: 0, cumDy: 0
      };
      this._setJoystickArrowsDirs(this._getWallGrabPerpArrows(wall));
    } else if (mode === 'wallEndpoint') {
      const wall = info.wall, room = info.room;
      const doorSnapshots = [];
      if (room) {
        for (const door of room.doors) {
          if (door.wall === wall) doorSnapshots.push({ door, hingePos: this.paramToPoint(wall, door.hingeParam), endPos: this.paramToPoint(wall, door.endParam) });
        }
      }
      this._selectHandleInfo = { ...info, doorSnapshots };
      this._setJoystickArrowsEnabled(true); // All 4 arrows
    } else if (mode === 'wallCorner') {
      const wall = info.wall, room = info.room, whichEnd = info.whichEnd;
      const pt = wall[whichEnd];
      const key = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
      const cornerKey = key(pt);
      const affectedWalls = [];
      if (room) {
        for (const w of room.walls) {
          if (key(w.start) === cornerKey) affectedWalls.push({ wall: w, whichEnd: 'start', origStartX: w.start.x, origStartY: w.start.y, origEndX: w.end.x, origEndY: w.end.y });
          else if (key(w.end) === cornerKey) affectedWalls.push({ wall: w, whichEnd: 'end', origStartX: w.start.x, origStartY: w.start.y, origEndX: w.end.x, origEndY: w.end.y });
        }
      }
      const doorSnapshots = [];
      if (room) {
        for (const aw of affectedWalls) {
          for (const door of room.doors) {
            if (door.wall === aw.wall) doorSnapshots.push({ door, hingePos: this.paramToPoint(aw.wall, door.hingeParam), endPos: this.paramToPoint(aw.wall, door.endParam) });
          }
        }
      }
      this._selectHandleInfo = { wall, room, whichEnd, pt: { x: pt.x, y: pt.y }, affectedWalls, doorSnapshots, origX: pt.x, origY: pt.y };
      this._setJoystickArrowsEnabled(true); // All 4 arrows
    } else if (mode === 'resize') {
      this._selectHandleInfo = info;
      this._setJoystickArrowsDirs(this._getResizeArrows(info.handle));
    } else if (mode === 'rotate') {
      this._selectHandleInfo = info;
      this._enterRotateDial();
    } else {
      this._selectHandleInfo = info;
      this._setJoystickArrowsEnabled(true);
    }
    this._updateJoystickVisibility();
    this._clearArrowPulse();
    this._clearCheckPulse();
    this._startArrowPulse(3000);
    this._startCheckPulse(3000);
    if (navigator.vibrate) navigator.vibrate(30);
    // Pan to center the activated handle on mobile
    if (this._isMobile) {
      let px, py;
      if (mode === 'wallGrab' && this._selectHandleInfo) {
        const w = this._selectHandleInfo.wall;
        const mid = this._getWallGrabHandle(w, this._selectHandleInfo.room);
        if (mid) { px = mid.wx; py = mid.wy; }
        else { px = (w.start.x + w.end.x) / 2; py = (w.start.y + w.end.y) / 2; }
      } else if (mode === 'wallEndpoint' && info) {
        const pt = info.whichEnd === 'start' ? info.wall.start : info.wall.end;
        px = pt.x; py = pt.y;
      } else if (mode === 'wallCorner' && this._selectHandleInfo) {
        px = this._selectHandleInfo.pt.x; py = this._selectHandleInfo.pt.y;
      } else if (mode === 'resize' && info) {
        const handles = this._getResizeHandles(info.obj);
        const h = handles.find(rh => rh.name === info.handle);
        if (h) { px = h.wx; py = h.wy; }
      } else if (mode === 'rotate' && info) {
        const rotH = this._getRotateHandle(info.obj);
        if (rotH) { px = rotH.wx; py = rotH.wy; }
      }
      if (px !== undefined) this._mobilePanToWorldPoint(px, py);
    }
  }

  _deactivateSelectHandle() {
    if (!this._selectHandleMode) return;
    if (this._selectRotateDialActive) this._exitRotateDial();
    this._selectHandleMode = null;
    this._selectHandleInfo = null;
    this._selectHandleCycleList = [];
    this._selectHandleCycleIndex = 0;
    this._setJoystickArrowsEnabled(true);
    this._clearArrowPulse();
    this._clearCheckPulse();
    // Restore checkmark to active state
    const placeBtn = document.querySelector('.ring-center[data-dir="place"]');
    if (placeBtn) placeBtn.classList.remove('inactive');
    this._updateJoystickVisibility();
  }

  _nudgeSelectHandle(dir) {
    this._restartJoystickPulse();
    const mode = this._selectHandleMode;
    const info = this._selectHandleInfo;
    if (!mode || !info) return;
    const step = this._currentGridStep();
    let dx = 0, dy = 0;
    if (dir === 'up') dy = -step;
    else if (dir === 'down') dy = step;
    else if (dir === 'left') dx = -step;
    else if (dir === 'right') dx = step;

    if (mode === 'wholeMove') {
      this._nudgeSelection(dir);
      return;
    }

    if (mode === 'objectMove') {
      this._nudgeSelection(dir);
      return;
    }

    if (mode === 'wallEndpoint') {
      const wall = info.wall, whichEnd = info.whichEnd;
      const pt = whichEnd === 'start' ? wall.start : wall.end;
      const newX = pt.x + dx, newY = pt.y + dy;
      let snapped = this.snapToGrid(newX, newY);
      info.snapTarget = null;
      // Check for snap to open endpoints (unclosed rooms)
      if (typeof this._findSnapEndpoint === 'function') {
        info.snapTarget = this._findSnapEndpoint(snapped, wall, info.room);
      }
      // For wall segment endpoints: also snap to room walls and other segment walls
      if (!info.snapTarget && info.room && info.room.segments && typeof this._findWallSurfaceSnap === 'function') {
        const isLine = (info.room.type || 'wall') === 'line';
        const rawPos = { x: newX, y: newY };
        const snap = this._findWallSurfaceSnap(snapped, wall, info.room, { rawPos, isLine });
        if (snap) {
          if (isLine && snap.snapType === 'midpoint' && snap.room && snap.room.walls && typeof this._quantizeWallEdgeSnap === 'function') {
            const edgePt = this._quantizeWallEdgeSnap(snap.wall, snap.param, rawPos, snap.room);
            snap.x = edgePt.x; snap.y = edgePt.y;
          } else if (snap.snapType === 'midpoint' && typeof this._quantizeWallSnap === 'function') {
            const qPt = this._quantizeWallSnap(snap.wall, snap.param);
            snap.x = qPt.x; snap.y = qPt.y;
          }
          info.snapTarget = snap;
        }
      }
      if (info.snapTarget) snapped = { x: info.snapTarget.x, y: info.snapTarget.y };
      // Check min wall length for doors
      const anchor = whichEnd === 'start' ? wall.end : wall.start;
      const newLen = Math.sqrt((snapped.x - anchor.x) ** 2 + (snapped.y - anchor.y) ** 2);
      const minLen = info.room ? this._getMinWallLengthForDoors(wall, info.room) : 0;
      if (minLen > 0 && newLen < minLen) return; // Would shrink too small for doors
      pt.x = snapped.x; pt.y = snapped.y;
      // Recompute door params
      if (info.doorSnapshots && info.doorSnapshots.length > 0) {
        this._recomputeAllDoorParamsFromSnapshots(info.doorSnapshots);
      }
      this._mobilePanCompensate(dx, dy);
      this.updateMeasurements(); this.render();
      return;
    }

    if (mode === 'wallCorner') {
      const targetX = info.pt.x + dx, targetY = info.pt.y + dy;
      const snapped = this.snapToGrid(targetX, targetY);
      const sdx = snapped.x - info.pt.x, sdy = snapped.y - info.pt.y;
      if (Math.abs(sdx) < 1e-6 && Math.abs(sdy) < 1e-6) return;
      // Check min wall lengths
      if (info.room) {
        for (const aw of info.affectedWalls) {
          const minLen = this._getMinWallLengthForDoors(aw.wall, info.room);
          if (minLen <= 0) continue;
          const movingEnd = aw.whichEnd;
          const fixed = movingEnd === 'start' ? aw.wall.end : aw.wall.start;
          const newMoving = { x: (movingEnd === 'start' ? aw.wall.start.x : aw.wall.end.x) + sdx, y: (movingEnd === 'start' ? aw.wall.start.y : aw.wall.end.y) + sdy };
          const newLen = Math.sqrt((newMoving.x - fixed.x) ** 2 + (newMoving.y - fixed.y) ** 2);
          if (newLen < minLen) return;
        }
      }
      // Apply movement
      for (const aw of info.affectedWalls) {
        if (aw.whichEnd === 'start') { aw.wall.start.x += sdx; aw.wall.start.y += sdy; }
        else { aw.wall.end.x += sdx; aw.wall.end.y += sdy; }
      }
      info.pt.x = snapped.x; info.pt.y = snapped.y;
      if (info.doorSnapshots && info.doorSnapshots.length > 0) {
        this._recomputeAllDoorParamsFromSnapshots(info.doorSnapshots);
      }
      this._mobilePanCompensate(sdx, sdy);
      this.updateMeasurements(); this.render();
      return;
    }

    if (mode === 'wallGrab') {
      const wall = info.wall;
      // Get perpendicular direction
      const wdx = info.origEndX - info.origStartX, wdy = info.origEndY - info.origStartY;
      const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
      if (wLen === 0) return;
      const perpX = -wdy / wLen, perpY = wdx / wLen;
      // Project arrow direction onto perpendicular
      let arrowX = 0, arrowY = 0;
      if (dir === 'up') arrowY = -1;
      else if (dir === 'down') arrowY = 1;
      else if (dir === 'left') arrowX = -1;
      else if (dir === 'right') arrowX = 1;
      const proj = arrowX * perpX + arrowY * perpY;
      const sign = proj >= 0 ? 1 : -1;
      const moveDx = perpX * step * sign, moveDy = perpY * step * sign;
      // Accumulate
      const newCumDx = info.cumDx + moveDx, newCumDy = info.cumDy + moveDy;
      // Compute new wall positions from originals
      const newStartX = info.origStartX + newCumDx, newStartY = info.origStartY + newCumDy;
      const newEndX = info.origEndX + newCumDx, newEndY = info.origEndY + newCumDy;
      // Check min wall length for doors on connected walls
      const key = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
      const checkConnected = (connOrig, origPtX, origPtY, newPtX, newPtY) => {
        const origMK = key({ x: origPtX, y: origPtY });
        for (const co of connOrig) {
          const minLen = info.room ? this._getMinWallLengthForDoors(co.wall, info.room) : 0;
          if (minLen <= 0) continue;
          const coSK = key({ x: co.startX, y: co.startY });
          const coEK = key({ x: co.endX, y: co.endY });
          let fixedPt;
          if (coSK === origMK) fixedPt = { x: co.endX, y: co.endY };
          else if (coEK === origMK) fixedPt = { x: co.startX, y: co.startY };
          else continue;
          const cdx = newPtX - fixedPt.x, cdy = newPtY - fixedPt.y;
          if (Math.sqrt(cdx * cdx + cdy * cdy) < minLen) return false;
        }
        return true;
      };
      if (!checkConnected(info.connStartOrig, info.origStartX, info.origStartY, newStartX, newStartY) ||
          !checkConnected(info.connEndOrig, info.origEndX, info.origEndY, newEndX, newEndY)) {
        return; // Would violate door constraints
      }
      // Apply
      info.cumDx = newCumDx; info.cumDy = newCumDy;
      wall.start.x = newStartX; wall.start.y = newStartY;
      wall.end.x = newEndX; wall.end.y = newEndY;
      // Update connected walls
      for (const co of info.connStartOrig) {
        const origSK = key({ x: info.origStartX, y: info.origStartY });
        if (key({ x: co.startX, y: co.startY }) === origSK) { co.wall.start.x = wall.start.x; co.wall.start.y = wall.start.y; }
        else if (key({ x: co.endX, y: co.endY }) === origSK) { co.wall.end.x = wall.start.x; co.wall.end.y = wall.start.y; }
      }
      for (const co of info.connEndOrig) {
        const origEK = key({ x: info.origEndX, y: info.origEndY });
        if (key({ x: co.startX, y: co.startY }) === origEK) { co.wall.start.x = wall.end.x; co.wall.start.y = wall.end.y; }
        else if (key({ x: co.endX, y: co.endY }) === origEK) { co.wall.end.x = wall.end.x; co.wall.end.y = wall.end.y; }
      }
      if (info.doorSnapshots && info.doorSnapshots.length > 0) {
        this._recomputeAllDoorParamsFromSnapshots(info.doorSnapshots);
      }
      this._mobilePanCompensate(moveDx, moveDy);
      this.updateMeasurements(); this.render();
      return;
    }

    if (mode === 'resize') {
      const obj = info.obj;
      // Capture handle position before resize for pan compensation
      const beforeHandles = this._getResizeHandles(obj);
      const beforeH = beforeHandles.find(h => h.name === info.handle);
      if (obj.type === 'rectangle' || obj.type === 'equipment') {
        const rot = obj.rotation || 0;
        const cosR = Math.cos(-rot), sinR = Math.sin(-rot);
        // Convert dx, dy to local space
        const ldx = dx * cosR - dy * sinR;
        const ldy = dx * sinR + dy * cosR;
        const h = info.handle;
        let dw = 0, dd = 0;
        if (h.includes('e')) dw = ldx;
        if (h.includes('w')) dw = -ldx;
        if (h.includes('s')) dd = ldy;
        if (h.includes('n')) dd = -ldy;
        const newW = Math.max(step, obj.width + dw);
        const newD = Math.max(step, obj.depth + dd);
        const actDw = newW - obj.width, actDd = newD - obj.depth;
        // Shift center to keep anchor (opposite handle) fixed
        let localShiftX = 0, localShiftY = 0;
        if (h.includes('e')) localShiftX = actDw / 2;
        if (h.includes('w')) localShiftX = -actDw / 2;
        if (h.includes('s')) localShiftY = actDd / 2;
        if (h.includes('n')) localShiftY = -actDd / 2;
        // Convert local shift back to world
        const worldShiftX = localShiftX * Math.cos(rot) - localShiftY * Math.sin(rot);
        const worldShiftY = localShiftX * Math.sin(rot) + localShiftY * Math.cos(rot);
        obj.width = newW; obj.depth = newD;
        obj.x += worldShiftX; obj.y += worldShiftY;
      } else if (obj.type === 'circle') {
        // Resize circle based on direction
        const h = info.handle;
        let dd = 0;
        if (h === 'n' || h === 's') dd = (dir === 'up' || dir === 'down') ? step : 0;
        else dd = (dir === 'left' || dir === 'right') ? step : 0;
        // Grow or shrink based on direction relative to handle
        if ((h === 'n' && dir === 'up') || (h === 's' && dir === 'down') ||
            (h === 'e' && dir === 'right') || (h === 'w' && dir === 'left')) {
          obj.diameter = Math.max(step, obj.diameter + step);
        } else {
          obj.diameter = Math.max(step, obj.diameter - step);
        }
      }
      // Compensate pan by actual handle movement delta
      if (beforeH) {
        const afterHandles = this._getResizeHandles(obj);
        const afterH = afterHandles.find(h => h.name === info.handle);
        if (afterH) this._mobilePanCompensate(afterH.wx - beforeH.wx, afterH.wy - beforeH.wy);
      }
      this.updateMeasurements(); this.render();
      return;
    }

    if (mode === 'rotate') {
      // Arrow tap = 90° rotation
      const obj = info.obj;
      const delta = (dir === 'right' || dir === 'down') ? Math.PI / 2 : -Math.PI / 2;
      obj.rotation = (obj.rotation || 0) + delta;
      // Update dial indicator
      const dpad = document.getElementById('joystickDpad');
      if (dpad) {
        const ringEl = dpad.closest('.joystick-ring');
        if (ringEl) this._updateRotateDialIndicator(ringEl, obj.rotation);
      }
      this.render();
      return;
    }
  }

  _enterRotateDial() {
    this._selectRotateDialActive = true;
    const dpad = document.getElementById('joystickDpad');
    if (!dpad) return;
    const ringEl = dpad.closest('.joystick-ring');
    if (!ringEl) return;
    ringEl.classList.add('rotate-dial-mode');
    this._setJoystickArrowsEnabled(true); // Arrows still do 90° snaps

    // Create rotation indicator dot + angle label on the ring
    let indicator = ringEl.querySelector('.rotate-dial-indicator');
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'rotate-dial-indicator';
      ringEl.appendChild(indicator);
    }
    let angleLabel = ringEl.querySelector('.rotate-dial-angle');
    if (!angleLabel) {
      angleLabel = document.createElement('div');
      angleLabel.className = 'rotate-dial-angle';
      ringEl.appendChild(angleLabel);
    }
    indicator.style.display = 'block';
    angleLabel.style.display = 'block';

    // Create 90° cross-hair lines on the ring
    let lineH = ringEl.querySelector('.rotate-dial-line-h');
    let lineV = ringEl.querySelector('.rotate-dial-line-v');
    if (!lineH) {
      lineH = document.createElement('div');
      lineH.className = 'rotate-dial-line rotate-dial-line-h';
      ringEl.appendChild(lineH);
    }
    if (!lineV) {
      lineV = document.createElement('div');
      lineV.className = 'rotate-dial-line rotate-dial-line-v';
      ringEl.appendChild(lineV);
    }
    lineH.style.display = 'block';
    lineV.style.display = 'block';

    // Position indicator based on current object rotation
    const info = this._selectHandleInfo;
    const currentRot = info && info.obj ? (info.obj.rotation || 0) : 0;
    this._updateRotateDialIndicator(ringEl, currentRot);

    // Track which snap detents we've vibrated for
    this._rotateDialLastSnap = null;
    this._rotateDialDragging = false;
    this._rotateDialStartAngle = null;
    this._rotateDialStartRotation = null;

    // Touch overlay for drag-to-rotate
    // Track touch start on the ring for drag detection
    // We don't preventDefault on start — let arrow/checkmark taps work normally
    // Only once the finger moves enough angular distance do we switch to dial-drag mode
    const onTouchStart = (e) => {
      if (!this._selectRotateDialActive) return;
      const touch = e.touches[0];
      const rect = ringEl.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = touch.clientX - cx, dy = touch.clientY - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      // Ignore touches outside the ring
      if (dist > rect.width / 2 + 20) return;
      // Ignore touches on the center checkmark area
      if (dist < 24) return;
      // Record start position for drag detection — don't claim the touch yet
      this._rotateDialPending = true;
      this._rotateDialPendingAngle = Math.atan2(dy, dx);
      this._rotateDialPendingRotation = info && info.obj ? (info.obj.rotation || 0) : 0;
      this._rotateDialDragging = false;
      this._rotateDialLastSnap = null;
    };

    const onTouchMove = (e) => {
      if (!this._selectRotateDialActive) return;
      if (!this._rotateDialPending && !this._rotateDialDragging) return;
      const touch = e.touches[0];
      const rect = ringEl.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = touch.clientX - cx, dy = touch.clientY - cy;
      const currentAngle = Math.atan2(dy, dx);
      // If still pending, check if finger has moved enough angularly to be a drag
      if (this._rotateDialPending && !this._rotateDialDragging) {
        let angleDiff = Math.abs(currentAngle - this._rotateDialPendingAngle);
        if (angleDiff > Math.PI) angleDiff = 2 * Math.PI - angleDiff;
        if (angleDiff < 0.05) return; // ~3° dead zone — still could be a tap
        // Commit to drag mode
        this._rotateDialDragging = true;
        this._rotateDialPending = false;
        this._rotateDialStartAngle = this._rotateDialPendingAngle;
        this._rotateDialStartRotation = this._rotateDialPendingRotation;
        // Cancel any pending arrow hold timers since we're dragging now
        clearTimeout(this._joystickHoldTimer);
        clearTimeout(this._joystickHoldInterval);
        this._joystickHoldTimer = null;
        this._joystickHoldInterval = null;
      }
      if (!this._rotateDialDragging) return;
      e.preventDefault();
      let delta = currentAngle - this._rotateDialStartAngle;
      // Normalize delta to [-PI, PI]
      while (delta > Math.PI) delta -= 2 * Math.PI;
      while (delta < -Math.PI) delta += 2 * Math.PI;
      let newRot = this._rotateDialStartRotation + delta;
      // Snap detents at 45° increments (±5° threshold)
      const snapAngles = [
        0, Math.PI / 4, Math.PI / 2, 3 * Math.PI / 4, Math.PI,
        -Math.PI / 4, -Math.PI / 2, -3 * Math.PI / 4, -Math.PI,
        5 * Math.PI / 4, 3 * Math.PI / 2, 7 * Math.PI / 4,
        -5 * Math.PI / 4, -3 * Math.PI / 2, -7 * Math.PI / 4,
        2 * Math.PI, -2 * Math.PI
      ];
      const snapThreshold = 5 * Math.PI / 180; // 5 degrees
      let snapped = false;
      let snapKey = null;
      for (const sa of snapAngles) {
        if (Math.abs(newRot - sa) < snapThreshold) {
          newRot = sa;
          snapKey = Math.round(sa * 1000);
          snapped = true;
          break;
        }
      }
      if (snapped && snapKey !== this._rotateDialLastSnap) {
        if (navigator.vibrate) navigator.vibrate(10);
        this._rotateDialLastSnap = snapKey;
      } else if (!snapped) {
        this._rotateDialLastSnap = null;
      }
      if (info && info.obj) {
        info.obj.rotation = newRot;
        this._updateRotateDialIndicator(ringEl, newRot);
        this.render();
      }
    };

    const onTouchEnd = (e) => {
      this._rotateDialPending = false;
      if (!this._rotateDialDragging) return;
      this._rotateDialDragging = false;
      this._rotateDialStartAngle = null;
      this._rotateDialStartRotation = null;
    };

    // Store references for cleanup
    this._rotateDialTouchStart = onTouchStart;
    this._rotateDialTouchMove = onTouchMove;
    this._rotateDialTouchEnd = onTouchEnd;
    ringEl.addEventListener('touchstart', onTouchStart, { passive: false, capture: true });
    ringEl.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    ringEl.addEventListener('touchend', onTouchEnd, { passive: false, capture: true });
  }

  _updateRotateDialIndicator(ringEl, rotation) {
    const indicator = ringEl.querySelector('.rotate-dial-indicator');
    const angleLabel = ringEl.querySelector('.rotate-dial-angle');
    if (!indicator) return;
    // Position indicator dot on the ring edge at the object's rotation angle
    // Rotation 0 = right (3 o'clock), but for UX let's map object rotation to ring position
    // Object rotation: 0 = upright, positive = clockwise
    // Ring angle: CSS rotation where 0 = top (12 o'clock)
    const ringRadius = 54; // px — between inner (23px) and outer (66px) ring
    const centerX = 66; // half of 132px ring
    const centerY = 66;
    // Map object rotation to ring: -PI/2 offset since atan2 0 = right but we want 0 = top
    const visualAngle = rotation - Math.PI / 2;
    const dotX = centerX + ringRadius * Math.cos(visualAngle);
    const dotY = centerY + ringRadius * Math.sin(visualAngle);
    indicator.style.left = dotX + 'px';
    indicator.style.top = dotY + 'px';
    // Update angle label
    if (angleLabel) {
      let deg = rotation * 180 / Math.PI;
      // Normalize to 0-360
      deg = ((deg % 360) + 360) % 360;
      angleLabel.textContent = Math.round(deg) + '°';
    }
  }

  _exitRotateDial() {
    this._selectRotateDialActive = false;
    this._rotateDialDragging = false;
    this._rotateDialPending = false;
    const dpad = document.getElementById('joystickDpad');
    if (!dpad) return;
    const ringEl = dpad.closest('.joystick-ring');
    if (!ringEl) return;
    ringEl.classList.remove('rotate-dial-mode');
    // Remove touch listeners
    if (this._rotateDialTouchStart) {
      ringEl.removeEventListener('touchstart', this._rotateDialTouchStart, { passive: false, capture: true });
      ringEl.removeEventListener('touchmove', this._rotateDialTouchMove, { passive: false, capture: true });
      ringEl.removeEventListener('touchend', this._rotateDialTouchEnd, { passive: false, capture: true });
      this._rotateDialTouchStart = null;
      this._rotateDialTouchMove = null;
      this._rotateDialTouchEnd = null;
    }
    // Hide indicator + angle label + 90° lines
    const indicator = ringEl.querySelector('.rotate-dial-indicator');
    const angleLabel = ringEl.querySelector('.rotate-dial-angle');
    if (indicator) indicator.style.display = 'none';
    if (angleLabel) angleLabel.style.display = 'none';
    const lineH = ringEl.querySelector('.rotate-dial-line-h');
    const lineV = ringEl.querySelector('.rotate-dial-line-v');
    if (lineH) lineH.style.display = 'none';
    if (lineV) lineV.style.display = 'none';
  }


  _moveDoorAlongWall(direction) {
    // direction = +1 (toward wall.end) or -1 (toward wall.start)
    // dm.param is the CENTER of the door on the wall
    const dm = this._doorMobile;
    if (!dm) return;
    const oldPoint = dm.point ? { x: dm.point.x, y: dm.point.y } : null;
    dm.travelDir = direction; // Track logical travel direction for hold-repeat
    const wall = dm.wall;
    const wallLen = this.getWallLength(wall);
    if (wallLen === 0) return;
    const step = this._currentGridStep();
    const paramStep = step / wallLen;
    const doorWidthFeet = this.doorWidth / 12;
    const halfParam = wallLen > 0 ? (doorWidthFeet / 2) / wallLen : 0;
    const minParam = halfParam;        // center can't go closer than this to wall start
    const maxParam = 1 - halfParam;    // center can't go closer than this to wall end
    let newParam = dm.param + direction * paramStep;
    if (newParam >= minParam && newParam <= maxParam) {
      // Still on same wall within bounds — snap and update
      dm.param = this.snapParamToGrid(wall, newParam);
      // Re-clamp after snap (snap might push slightly out of bounds)
      dm.param = Math.max(minParam, Math.min(maxParam, dm.param));
      dm.point = this.paramToPoint(wall, dm.param);
      this.mouseWorldRaw = { x: dm.point.x, y: dm.point.y };
      this.mouseWorld = { x: dm.point.x, y: dm.point.y };
      if (oldPoint) this._mobilePanCompensate(dm.point.x - oldPoint.x, dm.point.y - oldPoint.y);
      this.render();
      return;
    }
    // Past an endpoint (door edge would exit wall) — try to snake to next wall
    const whichEnd = direction > 0 ? 'end' : 'start';
    const nextWall = this._findNextWallAtCorner(wall, dm.room, whichEnd);
    if (!nextWall) {
      // Dead end — clamp center so door edge is flush with wall endpoint
      dm.param = direction > 0 ? maxParam : minParam;
      dm.point = this.paramToPoint(wall, dm.param);
      this.mouseWorldRaw = { x: dm.point.x, y: dm.point.y };
      this.mouseWorld = { x: dm.point.x, y: dm.point.y };
      if (oldPoint) this._mobilePanCompensate(dm.point.x - oldPoint.x, dm.point.y - oldPoint.y);
      this.render();
      return;
    }
    // Transition to next wall: figure out which end we entered from
    const cornerPt = whichEnd === 'end' ? wall.end : wall.start;
    const key = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
    const ck = key(cornerPt);
    const enteredFromStart = key(nextWall.start) === ck;
    const overshoot = Math.abs(newParam - (direction > 0 ? maxParam : minParam));
    const nextWallLen = this.getWallLength(nextWall);
    const nextHalfParam = nextWallLen > 0 ? (doorWidthFeet / 2) / nextWallLen : 0;
    let nextParam;
    if (enteredFromStart) {
      nextParam = nextWallLen > 0 ? Math.min(1 - nextHalfParam, nextHalfParam + (overshoot * wallLen) / nextWallLen) : nextHalfParam;
    } else {
      nextParam = nextWallLen > 0 ? Math.max(nextHalfParam, (1 - nextHalfParam) - (overshoot * wallLen) / nextWallLen) : (1 - nextHalfParam);
    }
    dm.wall = nextWall;
    dm.travelDir = enteredFromStart ? 1 : -1; // Continue forward on new wall
    dm.param = this.snapParamToGrid(nextWall, nextParam);
    dm.param = Math.max(nextHalfParam, Math.min(1 - nextHalfParam, dm.param));
    dm.point = this.paramToPoint(nextWall, dm.param);
    // Update room if the new wall belongs to a different room
    const newRoom = this._findRoomForWall(nextWall);
    if (newRoom) dm.room = newRoom;
    this.mouseWorldRaw = { x: dm.point.x, y: dm.point.y };
    this.mouseWorld = { x: dm.point.x, y: dm.point.y };
    if (oldPoint) this._mobilePanCompensate(dm.point.x - oldPoint.x, dm.point.y - oldPoint.y);
    // Update arrow directions for the new wall
    this._updateDoorMobileArrows();
    this.render();
  }

  _slideSelectedDoorAlongWall(travelDir) {
    // Slides a single selected door along its wall by one grid step, snaking at corners.
    // travelDir: +1 (toward wall.end) or -1 (toward wall.start)
    // Returns true if door moved (continue hold), false if dead end (stop hold).
    if (this.selectedDoors.size !== 1) return false;
    const door = this.selectedDoors.values().next().value;
    const wall = door.wall;
    const wallLen = this.getWallLength(wall);
    if (wallLen === 0) return false;
    // Capture door center world position before move for pan compensation
    const oldCenter = this.paramToPoint(wall, (door.hingeParam + door.endParam) / 2);
    const step = this._currentGridStep();
    const doorWidthFeet = door.width / 12;
    const halfParam = wallLen > 0 ? (doorWidthFeet / 2) / wallLen : 0;
    const paramStep = step / wallLen;
    const hingeFirst = door.hingeParam <= door.endParam;
    const doorParamWidth = Math.abs(door.endParam - door.hingeParam);
    // Current center param
    const centerParam = (door.hingeParam + door.endParam) / 2;
    const minCenter = halfParam;
    const maxCenter = 1 - halfParam;
    let newCenter = centerParam + travelDir * paramStep;
    if (newCenter >= minCenter && newCenter <= maxCenter) {
      // Still on same wall — snap and update
      newCenter = this.snapParamToGrid(wall, newCenter);
      newCenter = Math.max(minCenter, Math.min(maxCenter, newCenter));
      const room = this._findRoomForWall(wall);
      const nHinge = hingeFirst ? newCenter - doorParamWidth / 2 : newCenter + doorParamWidth / 2;
      const nEnd = hingeFirst ? newCenter + doorParamWidth / 2 : newCenter - doorParamWidth / 2;
      const lo = Math.min(nHinge, nEnd), hi = Math.max(nHinge, nEnd);
      if (room && this._doorsOverlap(wall, room, lo, hi, door)) return false;
      door.hingeParam = nHinge;
      door.endParam = nEnd;
      const newCtr = this.paramToPoint(wall, (nHinge + nEnd) / 2);
      this._mobilePanCompensate(newCtr.x - oldCenter.x, newCtr.y - oldCenter.y);
      this.updateMeasurements();
      this.render();
      return true;
    }
    // Past wall boundary — try to snake to next wall
    const whichEnd = travelDir > 0 ? 'end' : 'start';
    const room = this._findRoomForWall(wall);
    const nextWall = room ? this._findNextWallAtCorner(wall, room, whichEnd) : null;
    if (!nextWall) {
      // Dead end — clamp to edge
      const clampedCenter = travelDir > 0 ? maxCenter : minCenter;
      const nHinge = hingeFirst ? clampedCenter - doorParamWidth / 2 : clampedCenter + doorParamWidth / 2;
      const nEnd = hingeFirst ? clampedCenter + doorParamWidth / 2 : clampedCenter - doorParamWidth / 2;
      const lo = Math.min(nHinge, nEnd), hi = Math.max(nHinge, nEnd);
      if (room && this._doorsOverlap(wall, room, lo, hi, door)) return false;
      door.hingeParam = nHinge;
      door.endParam = nEnd;
      const newCtr = this.paramToPoint(wall, (nHinge + nEnd) / 2);
      this._mobilePanCompensate(newCtr.x - oldCenter.x, newCtr.y - oldCenter.y);
      this.updateMeasurements();
      this.render();
      return false; // dead end — stop hold
    }
    // Transition to next wall
    const nextWallLen = this.getWallLength(nextWall);
    if (nextWallLen < doorWidthFeet - 0.01) return false; // door doesn't fit
    const cornerPt = whichEnd === 'end' ? wall.end : wall.start;
    const pkey = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
    const ck = pkey(cornerPt);
    const enteredFromStart = pkey(nextWall.start) === ck;
    const overshoot = Math.abs(newCenter - (travelDir > 0 ? maxCenter : minCenter));
    const nextHalfParam = nextWallLen > 0 ? (doorWidthFeet / 2) / nextWallLen : 0;
    const overshootParam = nextWallLen > 0 ? (overshoot * wallLen) / nextWallLen : 0;
    let nextCenter = enteredFromStart
      ? Math.min(1 - nextHalfParam, nextHalfParam + overshootParam)
      : Math.max(nextHalfParam, (1 - nextHalfParam) - overshootParam);
    nextCenter = Math.max(nextHalfParam, Math.min(1 - nextHalfParam, nextCenter));
    const nextParamWidth = nextWallLen > 0 ? doorWidthFeet / nextWallLen : 0;
    let nHinge, nEnd;
    if (hingeFirst) { nHinge = nextCenter - nextParamWidth / 2; nEnd = nextCenter + nextParamWidth / 2; }
    else { nEnd = nextCenter - nextParamWidth / 2; nHinge = nextCenter + nextParamWidth / 2; }
    // Clamp to [0,1]
    const nLo = Math.min(nHinge, nEnd), nHi = Math.max(nHinge, nEnd);
    if (nLo < 0) { nHinge -= nLo; nEnd -= nLo; }
    const nHi2 = Math.max(nHinge, nEnd);
    if (nHi2 > 1) { const ov = nHi2 - 1; nHinge -= ov; nEnd -= ov; }
    // Overlap check on new wall
    const newRoom = this._findRoomForWall(nextWall);
    if (newRoom && this._doorsOverlap(nextWall, newRoom, Math.min(nHinge, nEnd), Math.max(nHinge, nEnd), door)) return false;
    // Move door from old room to new
    if (room) { const idx = room.doors.indexOf(door); if (idx !== -1) room.doors.splice(idx, 1); }
    door.wall = nextWall;
    door.hingeParam = nHinge;
    door.endParam = nEnd;
    if (newRoom && !newRoom.doors.includes(door)) newRoom.doors.push(door);
    // Update travel direction for continued hold
    this._selectedDoorTravelDir = enteredFromStart ? 1 : -1;
    // Don't update joystick arrows mid-hold — disabling the pressed button
    // fires pointerleave which kills the hold timer. Arrows update on pointer up.
    const newCtr = this.paramToPoint(nextWall, (nHinge + nEnd) / 2);
    this._mobilePanCompensate(newCtr.x - oldCenter.x, newCtr.y - oldCenter.y);
    this.updateMeasurements();
    this.render();
    return true;
  }

  _findNextWallAtCorner(wall, room, whichEnd) {
    const connected = this._getWallsConnectedAt(wall, room, whichEnd);
    if (connected.length === 0) return null;
    if (connected.length === 1) return connected[0];
    // T-intersection: pick the wall with smallest absolute turn angle (most "straight ahead")
    const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
    if (wLen === 0) return connected[0];
    // Incoming direction: direction we're traveling when we hit this end
    const inDirX = whichEnd === 'end' ? wdx / wLen : -wdx / wLen;
    const inDirY = whichEnd === 'end' ? wdy / wLen : -wdy / wLen;
    let best = null, bestAngle = Infinity;
    const key = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
    const cornerPt = whichEnd === 'end' ? wall.end : wall.start;
    const ck = key(cornerPt);
    for (const cw of connected) {
      // Determine outgoing direction on this connected wall
      const cwdx = cw.end.x - cw.start.x, cwdy = cw.end.y - cw.start.y;
      const cwLen = Math.sqrt(cwdx * cwdx + cwdy * cwdy);
      if (cwLen === 0) continue;
      // If corner is at cw.start, we'd travel from start→end; if at cw.end, from end→start
      let outDirX, outDirY;
      if (key(cw.start) === ck) { outDirX = cwdx / cwLen; outDirY = cwdy / cwLen; }
      else { outDirX = -cwdx / cwLen; outDirY = -cwdy / cwLen; }
      const angle = Math.abs(Math.atan2(inDirX * outDirY - inDirY * outDirX, inDirX * outDirX + inDirY * outDirY));
      if (angle < bestAngle) { bestAngle = angle; best = cw; }
    }
    return best || connected[0];
  }

  _updateDoorMobileArrows() {
    const dm = this._doorMobile;
    if (!dm) return;
    if (dm.phase === 1) {
      this._setJoystickArrowsDirs(this._getDoorWallArrows(dm.wall));
    } else if (dm.phase === 2) {
      // Phase 2: enable only the two wall-aligned arrows for end-direction selection
      this._setJoystickArrowsDirs(this._getDoorWallArrows(dm.wall));
    } else if (dm.phase === 3) {
      // Phase 3: enable perpendicular arrows for swing selection
      this._updateDoorSwingArrows();
    }
  }

  _updateDoorSwingArrows() {
    // Phase 3: enable only arrows that do something from current selection
    const dm = this._doorMobile;
    if (!dm || dm.phase !== 3 || !this.doorPlacement) return;
    const wall = this.doorPlacement.wall;
    const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
    if (wLen === 0) return;
    const wallDirX = wdx / wLen, wallDirY = wdy / wLen;
    const perpX = -wdy / wLen, perpY = wdx / wLen;
    const options = dm.swingOptions;
    const curIdx = dm.swingIdx != null ? dm.swingIdx : -1;
    const curOpt = dm.swingOption;
    const arrows = { up: false, down: false, left: false, right: false };
    const dirs = ['up', 'down', 'left', 'right'];
    for (const d of dirs) {
      let ax = 0, ay = 0;
      if (d === 'up') ay = -1;
      else if (d === 'down') ay = 1;
      else if (d === 'left') ax = -1;
      else if (d === 'right') ax = 1;
      const wallProj = ax * wallDirX + ay * wallDirY;
      const perpProj = ax * perpX + ay * perpY;
      if (curIdx === -1) {
        // At center: enable if perpendicular goes to a side with dots
        if (Math.abs(perpProj) >= 0.01) {
          const targetSign = perpProj >= 0 ? 1 : -1;
          if (options.some(o => o.perpSign === targetSign)) arrows[d] = true;
        }
      } else {
        const sideSign = curOpt.perpSign;
        const towardCenter = perpProj * sideSign < -0.01;
        const isParallel = Math.abs(wallProj) > Math.abs(perpProj);
        if (towardCenter && !isParallel) {
          arrows[d] = true; // goes to center
        } else if (Math.abs(wallProj) > 0.01) {
          // parallel: enable if there are other dots on same side
          if (options.some(o => o.perpSign === sideSign && o !== curOpt)) arrows[d] = true;
        }
      }
    }
    this._setJoystickArrowsEnabled(true);
    this._setJoystickArrowsDirs(arrows);
  }

  _cycleDoorEditSwing(dir) {
    const info = this._selectHandleInfo;
    if (!info || this._selectHandleMode !== 'doorSwingEdit') return;
    const door = info.door;
    const wall = door.wall;
    const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
    if (wLen === 0) return;
    const wallDirX = wdx / wLen, wallDirY = wdy / wLen;
    const perpX = -wdy / wLen, perpY = wdx / wLen;
    let arrowX = 0, arrowY = 0;
    if (dir === 'up') arrowY = -1;
    else if (dir === 'down') arrowY = 1;
    else if (dir === 'left') arrowX = -1;
    else if (dir === 'right') arrowX = 1;
    const wallProj = arrowX * wallDirX + arrowY * wallDirY;
    const perpProj = arrowX * perpX + arrowY * perpY;
    const options = info.swingOptions;
    const curIdx = info.swingIdx != null ? info.swingIdx : -1;
    const curOpt = info.swingOption;
    if (curIdx === -1) {
      if (Math.abs(perpProj) < 0.01) return;
      const targetSign = perpProj >= 0 ? 1 : -1;
      const sideOpts = options.filter(o => o.perpSign === targetSign);
      if (sideOpts.length > 0) {
        const target = sideOpts.find(o => o.hingeEnd === 'hinge') || sideOpts[0];
        info.swingIdx = options.indexOf(target);
        info.swingOption = target;
      }
    } else {
      const sideSign = curOpt.perpSign;
      const towardCenter = perpProj * sideSign < -0.01;
      const isParallel = Math.abs(wallProj) > Math.abs(perpProj);
      if (towardCenter && !isParallel) {
        info.swingIdx = -1;
        info.swingOption = null;
      } else if (Math.abs(wallProj) > 0.01) {
        const sameSide = options.filter(o => o.perpSign === sideSign && o !== curOpt);
        if (sameSide.length > 0) {
          info.swingOption = sameSide[0];
          info.swingIdx = options.indexOf(info.swingOption);
        }
      }
    }
  }

  _updateDoorEditSwingArrows() {
    const info = this._selectHandleInfo;
    if (!info || this._selectHandleMode !== 'doorSwingEdit') return;
    const door = info.door;
    const wall = door.wall;
    const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
    if (wLen === 0) return;
    const wallDirX = wdx / wLen, wallDirY = wdy / wLen;
    const perpX = -wdy / wLen, perpY = wdx / wLen;
    const options = info.swingOptions;
    const curIdx = info.swingIdx != null ? info.swingIdx : -1;
    const curOpt = info.swingOption;
    const arrows = { up: false, down: false, left: false, right: false };
    const dirs = ['up', 'down', 'left', 'right'];
    for (const d of dirs) {
      let ax = 0, ay = 0;
      if (d === 'up') ay = -1;
      else if (d === 'down') ay = 1;
      else if (d === 'left') ax = -1;
      else if (d === 'right') ax = 1;
      const wallProj = ax * wallDirX + ay * wallDirY;
      const perpProj = ax * perpX + ay * perpY;
      if (curIdx === -1) {
        if (Math.abs(perpProj) >= 0.01) {
          const targetSign = perpProj >= 0 ? 1 : -1;
          if (options.some(o => o.perpSign === targetSign)) arrows[d] = true;
        }
      } else {
        const sideSign = curOpt.perpSign;
        const towardCenter = perpProj * sideSign < -0.01;
        const isParallel = Math.abs(wallProj) > Math.abs(perpProj);
        if (towardCenter && !isParallel) arrows[d] = true;
        else if (Math.abs(wallProj) > 0.01) {
          if (options.some(o => o.perpSign === sideSign && o !== curOpt)) arrows[d] = true;
        }
      }
    }
    this._setJoystickArrowsEnabled(true);
    this._setJoystickArrowsDirs(arrows);
  }

  _drawDoorEditSwingMobile(scale) {
    const info = this._selectHandleInfo;
    if (!info || this._selectHandleMode !== 'doorSwingEdit') return;
    const door = info.door;
    const wall = door.wall;
    const doorWidthFeet = door.width / 12;
    const hingePoint = this.paramToPoint(wall, door.hingeParam);
    const endPoint = this.paramToPoint(wall, door.endParam);
    const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
    if (wLen === 0) return;
    const wallDirX = wdx / wLen, wallDirY = wdy / wLen;
    const perpX = -wallDirY, perpY = wallDirX;
    const doorRoom = info.room;
    const off = this._getDoorOffset(wall, perpX, perpY, doorRoom);
    const offHinge = { x: hingePoint.x + off.x, y: hingePoint.y + off.y };
    const offEnd = { x: endPoint.x + off.x, y: endPoint.y + off.y };
    const midX = (offHinge.x + offEnd.x) / 2, midY = (offHinge.y + offEnd.y) / 2;
    const options = info.swingOptions;
    const bestIdx = info.swingIdx != null ? info.swingIdx : -1;
    const closedHighlighted = (info.swingOption === null && bestIdx === -1);
    // Draw swing arcs and dots
    for (let i = 0; i < options.length; i++) {
      const opt = options[i];
      const highlighted = (i === bestIdx);
      const alpha = highlighted ? 0.45 : 0.15;
      if (door.type === 'double') {
        this._drawDoorSwingArc(offHinge, offEnd, doorWidthFeet, wallDirX, wallDirY, perpX, perpY, opt.perpSign, null, 'double', scale, '#006BCC', alpha);
      } else {
        this._drawDoorSwingArc(offHinge, offEnd, doorWidthFeet, wallDirX, wallDirY, perpX, perpY, opt.perpSign, opt.hingeEnd, 'single', scale, '#006BCC', alpha);
      }
      let tipX, tipY;
      if (door.type === 'double') {
        tipX = midX + perpX * opt.perpSign * (doorWidthFeet / 2);
        tipY = midY + perpY * opt.perpSign * (doorWidthFeet / 2);
      } else {
        const pivotPt = opt.hingeEnd === 'hinge' ? offHinge : offEnd;
        tipX = pivotPt.x + perpX * opt.perpSign * doorWidthFeet;
        tipY = pivotPt.y + perpY * opt.perpSign * doorWidthFeet;
      }
      this.ctx.fillStyle = highlighted ? 'rgba(0, 200, 0, 0.7)' : 'rgba(0, 107, 204, 0.3)';
      const dotR = 8;
      this.ctx.beginPath();
      this.ctx.arc(tipX, tipY, dotR / scale, 0, Math.PI * 2);
      this.ctx.fill();
    }
    // Closed dot at midpoint
    this.ctx.fillStyle = closedHighlighted ? 'rgba(0, 200, 0, 0.7)' : 'rgba(0, 107, 204, 0.3)';
    this.ctx.beginPath();
    this.ctx.arc(midX, midY, 8 / scale, 0, Math.PI * 2);
    this.ctx.fill();
    if (closedHighlighted) {
      this.ctx.save();
      this.ctx.strokeStyle = '#006BCC';
      this.ctx.lineWidth = 3 / scale;
      this.ctx.globalAlpha = 0.6;
      this.ctx.beginPath();
      this.ctx.moveTo(offHinge.x, offHinge.y);
      this.ctx.lineTo(offEnd.x, offEnd.y);
      this.ctx.stroke();
      this.ctx.restore();
    }
  }

  _getDoorSwingDots() {
    // Returns array of { swingIdx, option, tipX, tipY } for all swing option dots + closed dot
    const dp = this.doorPlacement;
    if (!dp) return [];
    const wall = dp.wall;
    const doorWidthFeet = this.doorWidth / 12;
    const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
    if (wLen === 0) return [];
    const perpX = -wdy / wLen, perpY = wdx / wLen;
    const off = this._getDoorOffset(wall, perpX, perpY, dp.room);
    const hingePoint = this.paramToPoint(wall, dp.hingeParam);
    const endPoint = this.paramToPoint(wall, dp.endParam);
    const offHinge = { x: hingePoint.x + off.x, y: hingePoint.y + off.y };
    const offEnd = { x: endPoint.x + off.x, y: endPoint.y + off.y };
    const midX = (offHinge.x + offEnd.x) / 2, midY = (offHinge.y + offEnd.y) / 2;
    const options = this._getSwingOptions();
    const dots = [];
    for (let i = 0; i < options.length; i++) {
      const opt = options[i];
      let tipX, tipY;
      if (this.doorType === 'double') {
        tipX = midX + perpX * opt.perpSign * (doorWidthFeet / 2);
        tipY = midY + perpY * opt.perpSign * (doorWidthFeet / 2);
      } else {
        const pivotPt = opt.hingeEnd === 'hinge' ? offHinge : offEnd;
        tipX = pivotPt.x + perpX * opt.perpSign * doorWidthFeet;
        tipY = pivotPt.y + perpY * opt.perpSign * doorWidthFeet;
      }
      dots.push({ swingIdx: i, option: opt, tipX, tipY });
    }
    // Closed option at door midpoint
    dots.push({ swingIdx: -1, option: null, tipX: midX, tipY: midY });
    return dots;
  }

  _doorMobileConfirmHinge() {
    const dm = this._doorMobile;
    if (!dm || dm.phase !== 1) return;
    const wall = dm.wall;
    const room = dm.room;
    const doorWidthFeet = this.doorWidth / 12;
    const wallLen = this.getWallLength(wall);
    if (wallLen < doorWidthFeet - 0.01) { this.updateStatus('Wall too short for this door.', { error: true }); return; }
    const halfParam = wallLen > 0 ? (doorWidthFeet / 2) / wallLen : 0.5;
    // dm.param is center — door spans [center - half, center + half]
    const leftParam = dm.param - halfParam;   // toward wall.start
    const rightParam = dm.param + halfParam;  // toward wall.end
    // Check overlap and constraints for the centered door span
    if (this._doorsOverlap(wall, room, leftParam, rightParam, null)) {
      this.updateStatus('No room for a door here — overlaps an existing door.', { error: true }); return;
    }
    if (this._doorBlockedByConstraints(wall, leftParam, rightParam, doorWidthFeet)) {
      this.updateStatus('No room for a door here.', { error: true }); return;
    }
    // Hinge can be at leftParam or rightParam — "direction" picks which end is hinge
    // dir +1 = hinge at leftParam (toward start), end at rightParam (toward end)
    // dir -1 = hinge at rightParam (toward end), end at leftParam (toward start)
    // For roller doors, skip straight to placement (no swing)
    if (this.doorType === 'roller') {
      dm.endDir = 1; // arbitrary — roller has no swing
      dm.endParam = rightParam;
      this.doorPlacement = { wall, room, hingeParam: leftParam, hingePoint: this.paramToPoint(wall, leftParam), phase: 2 };
      this.doorPlacement.endParam = rightParam;
      this._doorMobilePlaceDoor(null);
      return;
    }
    // For double doors, swing is perpendicular only (no hinge-end distinction), skip Phase 2
    if (this.doorType === 'double') {
      dm.endDir = 1;
      dm.endParam = rightParam;
      this.doorPlacement = { wall, room, hingeParam: leftParam, hingePoint: this.paramToPoint(wall, leftParam), phase: 2, endParam: rightParam };
      this._doorMobileConfirmEnd();
      return;
    }
    // Single doors on mobile: skip Phase 2 (hinge selection), go straight to swing selection
    // hingeParam = leftParam — swing option's hingeEnd determines actual pivot point
    this.doorPlacement = { wall, room, hingeParam: leftParam, hingePoint: this.paramToPoint(wall, leftParam), phase: 3, endParam: rightParam };
    dm.phase = 3;
    dm.swingOptions = this._getSwingOptions();
    dm.swingIdx = -1;
    dm.swingOption = null;
    this._animateRectLock(dm.point);
    this._updateDoorSwingArrows();
    this._clearCheckPulse();
    this._clearArrowPulse();
    this._startArrowPulse(1500);
    this._startCheckPulse(3000);
    if (navigator.vibrate) navigator.vibrate(50);
    this.updateStatus('Tap a dot or use arrows, then \u2713.');
    this.render();
  }

  _doorMobileConfirmEnd() {
    const dm = this._doorMobile;
    if (!dm) return;
    const wall = dm.wall;
    const doorWidthFeet = this.doorWidth / 12;
    const wallLen = this.getWallLength(wall);
    const halfParam = wallLen > 0 ? (doorWidthFeet / 2) / wallLen : 0.5;
    const leftParam = dm.param - halfParam;   // toward wall.start
    const rightParam = dm.param + halfParam;  // toward wall.end
    // For single doors: endDir picks which end is hinge
    if (dm.endDir == null && this.doorType === 'single') { this.updateStatus('Pick hinge side first.', { error: true }); return; }
    // Set hingeParam and endParam based on direction
    // dir +1 means hinge is at the "toward wall.end" side (rightParam)
    // dir -1 means hinge is at the "toward wall.start" side (leftParam)
    if (dm.endDir === 1) {
      this.doorPlacement.hingeParam = rightParam;
      this.doorPlacement.hingePoint = this.paramToPoint(wall, rightParam);
      this.doorPlacement.endParam = leftParam;
      dm.endParam = leftParam;
    } else {
      this.doorPlacement.hingeParam = leftParam;
      this.doorPlacement.hingePoint = this.paramToPoint(wall, leftParam);
      this.doorPlacement.endParam = rightParam;
      dm.endParam = rightParam;
    }
    // Roller doors: place immediately, skip Phase 3
    if (this.doorType === 'roller') {
      this._doorMobilePlaceDoor(null);
      return;
    }
    // Transition to Phase 3
    dm.phase = 3;
    this.doorPlacement.phase = 3;
    dm.swingOptions = this._getSwingOptions();
    dm.swingIdx = -1;
    dm.swingOption = null;
    this._animateRectLock(dm.point);
    this._updateDoorSwingArrows();
    this._clearCheckPulse();
    this._clearArrowPulse();
    this._startArrowPulse(1500);
    this._startCheckPulse(3000);
    if (navigator.vibrate) navigator.vibrate(50);
    this.updateStatus('Tap a dot or use arrows, then \u2713.');
    this.render();
  }

  _doorMobileCycleSwing(dir) {
    const dm = this._doorMobile;
    if (!dm || dm.phase !== 3 || !this.doorPlacement) return;
    const wall = this.doorPlacement.wall;
    const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
    if (wLen === 0) return;
    const wallDirX = wdx / wLen, wallDirY = wdy / wLen;
    const perpX = -wdy / wLen, perpY = wdx / wLen;
    // Arrow direction
    let arrowX = 0, arrowY = 0;
    if (dir === 'up') arrowY = -1;
    else if (dir === 'down') arrowY = 1;
    else if (dir === 'left') arrowX = -1;
    else if (dir === 'right') arrowX = 1;
    // Decompose arrow into wall-parallel and wall-perpendicular components
    const wallProj = arrowX * wallDirX + arrowY * wallDirY;
    const perpProj = arrowX * perpX + arrowY * perpY;
    const options = dm.swingOptions;
    const curIdx = dm.swingIdx != null ? dm.swingIdx : -1;
    const curOpt = dm.swingOption;
    if (curIdx === -1) {
      // At center (closed): perpendicular arrow goes to a side's first dot
      if (Math.abs(perpProj) < 0.01) return;
      const targetSign = perpProj >= 0 ? 1 : -1;
      const sideOpts = options.filter(o => o.perpSign === targetSign);
      if (sideOpts.length > 0) {
        const target = sideOpts.find(o => o.hingeEnd === 'hinge') || sideOpts[0];
        dm.swingIdx = options.indexOf(target);
        dm.swingOption = target;
      }
    } else {
      // On a swing dot
      const sideSign = curOpt.perpSign;
      const towardCenter = perpProj * sideSign < -0.01;
      const isParallel = Math.abs(wallProj) > Math.abs(perpProj);
      if (towardCenter && !isParallel) {
        // Perpendicular away from current side → go to center (closed)
        dm.swingIdx = -1;
        dm.swingOption = null;
      } else if (Math.abs(wallProj) > 0.01) {
        // Wall-parallel component → swap between dots on same side
        const sameSide = options.filter(o => o.perpSign === sideSign && o !== curOpt);
        if (sameSide.length > 0) {
          dm.swingOption = sameSide[0];
          dm.swingIdx = options.indexOf(dm.swingOption);
        }
      }
    }
  }

  _doorMobileConfirmSwing() {
    const dm = this._doorMobile;
    if (!dm || dm.phase !== 3) return;
    // swingOption is null = closed door, or has perpSign + optional hingeEnd
    this._doorMobilePlaceDoor(dm.swingOption || null);
  }

  _doorMobilePlaceDoor(swing) {
    const dm = this._doorMobile;
    if (!dm || !this.doorPlacement) return;
    // Capture placement snapshot BEFORE saveUndoState (references are still live)
    const placementSnap = this._captureDoorMobileSnapshot();
    this.saveUndoState();
    // Tag the undo entry so undo can restore Phase 3
    if (placementSnap) this.undoStack[this.undoStack.length - 1]._placementRestore = placementSnap;
    dm.room.doors.push({
      wall: dm.wall,
      hingeParam: this.doorPlacement.hingeParam,
      endParam: this.doorPlacement.endParam,
      type: this.doorType,
      width: this.doorWidth,
      swing: swing
    });
    this._doorMobileFinish('Door placed!');
  }

  _doorMobileFinish(statusMsg) {
    // Capture door info BEFORE clearing state
    const dm = this._doorMobile;
    const dp = this.doorPlacement;
    this._lastPlacedObject = {
      objectType: 'door',
      doorType: this.doorType,
      doorWidth: this.doorWidth,
      wall: dm ? dm.wall : null,
      room: dm ? dm.room : null,
      centerParam: dp ? (dp.hingeParam + dp.endParam) / 2 : null
    };
    this.doorPlacement = null;
    this._doorMobile = null;
    this._doorMobilePrompt = false;
    this._rectIntroAnimating = false;
    this._clearCheckPulse();
    this._clearArrowPulse();
    if (navigator.vibrate) navigator.vibrate(50);
    this.updateMeasurements();
    // Stay on objects button but allow panning
    this._enterDoorIdleState();
    this._showRepeatButton = true;
    this._updateFloatingRepeat();
    this.updateStatus(statusMsg || 'Door placed!');
    this.render();
  }

  _enterDoorIdleState() {
    this._showRepeatButton = false;
    this._resetDoorMobileState();
    this.mouseWorldRaw = null;
    this.mouseWorld = null;
    // Stay in door/objects mode with hand-like panning
    this.activeTool = 'door';
    this.mode = 'door';
    this.canvas.style.cursor = 'grab';
    document.getElementById('objectsToolBtn').classList.add('active');
    document.getElementById('selectToolBtn').classList.remove('active');
    document.getElementById('handToolBtn').classList.remove('active');
    document.getElementById('penToolBtn').classList.remove('active');
    document.getElementById('eraserBtn').classList.remove('active');
    // Clear all object selections so user must re-pick from scratch
    if (this._isMobileToolbar) {
      this._doorToolClicked = false;
      this._mobileObjectChosen = false;
      document.querySelectorAll('.objects-type-btn').forEach(b => b.classList.remove('active'));
      document.querySelectorAll('.door-type-btn').forEach(b => b.classList.remove('active'));
      document.getElementById('objectsDoorOptions').style.display = 'none';
      document.getElementById('objectsRectOptions').style.display = 'none';
      document.getElementById('objectsCircleOptions').style.display = 'none';
    }
    this._updateJoystickVisibility();
    this._updateFloatingDelete();
    this._updateFloatingCancel();
    this._updateFloatingRepeat();
  }

  _isDoorIdleState() {
    return this.activeTool === 'door' && !this._doorMobile && !this.mouseWorldRaw;
  }

  // Gray out checkmark when rect has no area; active when multi-dimensional
  _updateRectCheckmark() {
    const placeBtn = document.querySelector('.ring-center[data-dir="place"]');
    if (!placeBtn) return;
    if (this._rectCorner1 && this._rectCorner2) {
      const c1 = this._rectCorner1, c2 = this._rectCorner2;
      const hasArea = Math.abs(c1.x - c2.x) > 0.01 && Math.abs(c1.y - c2.y) > 0.01;
      placeBtn.classList.toggle('inactive', !hasArea);
      // If checkmark just became active and arrows are already pulsing, join the pulse
      const arrowsPulsing = document.querySelector('.ring-seg.rect-pulse-hint');
      if (hasArea && arrowsPulsing) {
        placeBtn.classList.add('rect-pulse-hint'); placeBtn.classList.toggle('rect-pulse-subtract', this.roomType === 'subtract');
      } else if (!hasArea) {
        placeBtn.classList.remove('rect-pulse-hint', 'rect-pulse-subtract');
      }
    } else {
      placeBtn.classList.remove('inactive');
    }
  }

  // Gray out checkmark when freehand wall has zero length; active when it has length
  _updateFreehandCheckmark() {
    if (!this._freehandAutoPlaced || !this._freehandStartConfirmed) return;
    const placeBtn = document.querySelector('.ring-center[data-dir="place"]');
    if (!placeBtn || !this.currentWall) return;
    const hasLength = this.currentWall.start.x !== this.currentWall.end.x || this.currentWall.start.y !== this.currentWall.end.y;
    placeBtn.classList.toggle('inactive', !hasLength);
    // If checkmark just became active and arrows are already pulsing, join the pulse
    const arrowsPulsing = document.querySelector('.ring-seg.rect-pulse-hint');
    if (hasLength && arrowsPulsing) {
      placeBtn.classList.add('rect-pulse-hint'); placeBtn.classList.toggle('rect-pulse-subtract', this.roomType === 'subtract');
    } else if (!hasLength) {
      placeBtn.classList.remove('rect-pulse-hint', 'rect-pulse-subtract');
    }
  }


  /* ── MOBILE GRID CENTERING ── */

  _getMobileDotScreenPos() {
    // Dead center horizontally, 2/5 from top (= 3/5 up from bottom)
    return { x: this._cssWidth / 2, y: this._cssHeight * 0.4 };
  }

  _centerGridForMobile() {
    // Pan so that world origin (0,0) snapped to grid lands at the target screen position
    const target = this._getMobileDotScreenPos();
    const snapped = this.snapToGrid(0, 0);
    const ws = this.worldToScreen(snapped.x, snapped.y);
    this.panX += target.x - ws.x;
    this.panY += target.y - ws.y;
  }

  _mobilePanCompensate(dx, dy) {
    if (!this._isMobile) return;
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    const a = this._canvasRotationRad || 0;
    if (a === 0) {
      this.panX -= dx * scale;
      this.panY -= dy * scale;
    } else {
      const cos = Math.cos(a), sin = Math.sin(a);
      this.panX -= (dx * cos - dy * sin) * scale;
      this.panY -= (dx * sin + dy * cos) * scale;
    }
  }

  _mobilePanToWorldPoint(wx, wy, duration = 220) {
    if (!this._isMobile) return;
    this._cancelPanAnimation();
    const target = this._getMobileDotScreenPos();
    const ws = this.worldToScreen(wx, wy);
    const targetPanX = this.panX + (target.x - ws.x);
    const targetPanY = this.panY + (target.y - ws.y);
    const startPanX = this.panX, startPanY = this.panY;
    const dPanX = targetPanX - startPanX, dPanY = targetPanY - startPanY;
    if (Math.abs(dPanX) < 0.5 && Math.abs(dPanY) < 0.5) return;
    const start = performance.now();
    const animate = (now) => {
      let t = (now - start) / duration;
      if (t >= 1) { t = 1; this._panAnimId = null; }
      const ease = 1 - (1 - t) * (1 - t) * (1 - t); // ease-out cubic
      this.panX = startPanX + dPanX * ease;
      this.panY = startPanY + dPanY * ease;
      this.render();
      if (t < 1) this._panAnimId = requestAnimationFrame(animate);
    };
    this._panAnimId = requestAnimationFrame(animate);
  }

  _cancelPanAnimation() {
    if (this._panAnimId) { cancelAnimationFrame(this._panAnimId); this._panAnimId = null; }
  }

  _getCycleItemWorldPos(item) {
    if (item.mode === 'wallEndpoint' || item.mode === 'wallCorner') {
      const pt = item.whichEnd === 'start' ? item.wall.start : item.wall.end;
      return { x: pt.x, y: pt.y };
    }
    if (item.mode === 'wallGrab') {
      const room = this._findRoomForWall(item.wall);
      const mid = this._getWallGrabHandle(item.wall, room);
      return mid ? { x: mid.wx, y: mid.wy } : null;
    }
    if (item.mode === 'doorGrab') {
      const midParam = (item.door.hingeParam + item.door.endParam) / 2;
      const midPt = this.paramToPoint(item.door.wall, midParam);
      return { x: midPt.x, y: midPt.y };
    }
    if (item.mode === 'resize') {
      const handles = this._getResizeHandles(item.obj);
      const h = handles.find(h => h.name === item.handle);
      return h ? { x: h.wx, y: h.wy } : null;
    }
    if (item.mode === 'rotate') {
      const rotH = this._getRotateHandle(item.obj);
      return rotH ? { x: rotH.wx, y: rotH.wy } : null;
    }
    return null;
  }

  /* ── MOBILE RECT AUTO-PLACE ── */

  _mobileRectAutoPlace() {
    // Continuous drawing: use last shape's end position if available
    const lastEnd = this._mobileLastEndWorld;
    this._mobileLastEndWorld = null;
    const target = this._getMobileDotScreenPos();
    let snapped;
    if (lastEnd) {
      snapped = this.snapToGrid(lastEnd.x, lastEnd.y);
    } else {
      const world = this.screenToWorld(target.x, target.y);
      snapped = this.snapToGrid(world.x, world.y);
    }
    // Re-center pan so the snapped grid point lands exactly at the target pixel
    // (skip pan adjustment for continuous drawing — animation coming later)
    if (!lastEnd) {
      const scale = this.basePixelsPerFoot * this.zoomLevel;
      this.panX = target.x - snapped.x * scale;
      this.panY = target.y - snapped.y * scale;
    }
    this._rectCorner1 = { x: snapped.x, y: snapped.y };
    this._rectAutoPlaced = true;
    this.mouseWorld = { x: snapped.x, y: snapped.y };
    const canvasEmpty = this.rooms.length === 0 && this.objects.length === 0;
    if (canvasEmpty) {
      // Empty canvas: auto-confirm corner 1, go straight to drawing
      this._rectCorner1Confirmed = true;
      this._rectCorner2 = { x: snapped.x, y: snapped.y };
      this._setJoystickArrowsEnabled(true);
      this._startArrowPulse(1500);
      this._updateRectCheckmark();
      this._updateJoystickVisibility();
      this.updateStatus('Stretch the rectangle, then tap \u2713 to finish');
      if (navigator.vibrate) navigator.vibrate(30);
    } else {
      // Non-empty canvas: show intro animation, allow repositioning before confirm
      this._rectCorner1Confirmed = false;
      this._rectIntroAnimating = true;
      this._rectIntroAnimStart = performance.now();
      this._animateRectIntro();
      this._updateJoystickVisibility();
      this._setJoystickArrowsEnabled(true);
      if (this._rectPulseTimer) clearTimeout(this._rectPulseTimer);
      this._rectPulseTimer = setTimeout(() => {
        const penBtn = document.getElementById('penToolBtn');
        if (penBtn && this.activeTool === 'pen' && this._rectCorner1 && !this._rectCorner1Confirmed) {
          penBtn.classList.add('rect-pulse-hint'); penBtn.classList.toggle('rect-pulse-subtract', this.roomType === 'subtract');
        }
      }, 3000);
      this._startCheckPulse(3000);
      this.updateStatus('Move the dot, then tap \u2713 to lock it in');
    }
    this._updateFloatingCancel();
    this._updateFloatingRepeat();
    this.render();
  }

  /* ── MOBILE FREEHAND AUTO-PLACE ── */

  _mobileFreehandAutoPlace() {
    // Continuous drawing: use last shape's end position if available
    const lastEnd = this._mobileLastEndWorld;
    this._mobileLastEndWorld = null;
    const target = this._getMobileDotScreenPos();
    let snapped;
    if (lastEnd) {
      snapped = this.snapToGrid(lastEnd.x, lastEnd.y);
    } else {
      const world = this.screenToWorld(target.x, target.y);
      snapped = this.snapToGrid(world.x, world.y);
    }
    // Re-center pan so the snapped grid point lands exactly at the target pixel
    // (skip pan adjustment for continuous drawing — keeps the view stable)
    if (!lastEnd) {
      const scale = this.basePixelsPerFoot * this.zoomLevel;
      this.panX = target.x - snapped.x * scale;
      this.panY = target.y - snapped.y * scale;
    }
    // Create currentWall with start = end at snapped position
    this.currentWall = { start: { x: snapped.x, y: snapped.y }, end: { x: snapped.x, y: snapped.y } };
    this.hasPlacedFirstPoint = true;
    this._freehandAutoPlaced = true;
    this.mouseWorld = { x: snapped.x, y: snapped.y };
    // Ensure fresh room
    if (!this.currentRoom || this.currentRoom.closed) this.promoteUnclosedRoom();
    if (!this.currentRoom) this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
    const canvasEmpty = this.rooms.length === 0 && this.objects.length === 0;
    if (canvasEmpty) {
      // Empty canvas: auto-confirm start, go straight to drawing
      this._freehandStartConfirmed = true;
      this._setJoystickArrowsEnabled(true);
      this._startArrowPulse(1500);
      this._updateFreehandCheckmark();
      this._updateJoystickVisibility();
      this.updateStatus('Stretch the wall, then tap \u2713 to confirm');
      if (navigator.vibrate) navigator.vibrate(30);
    } else {
      // Non-empty canvas: show intro animation, allow repositioning before confirm
      this._freehandStartConfirmed = false;
      this._rectIntroAnimating = true;
      this._rectIntroAnimStart = performance.now();
      this._animateRectIntro();
      this._updateJoystickVisibility();
      this._setJoystickArrowsEnabled(true);
      this._startCheckPulse(3000);
      this.updateStatus('Move the dot, then tap \u2713 to lock it in');
    }
    this._updateFloatingCancel();
    this._updateFloatingRepeat();
    this.render();
  }

  /* ── MOBILE DOOR AUTO-PLACE ── */

  _mobileDoorAutoPlace() {
    // Clear stale inactive class from checkmark (may linger from previous tool, e.g. rect drawing)
    const placeBtn = document.querySelector('.ring-center[data-dir="place"]');
    if (placeBtn) placeBtn.classList.remove('inactive');
    const target = this._getMobileDotScreenPos();
    const world = this.screenToWorld(target.x, target.y);
    // Find nearest wall anywhere on the canvas
    const nearest = this.findNearestWall(world, Infinity);
    if (!nearest) {
      this.updateStatus('Draw a room first, then switch to the door tool.', { error: true });
      this._doorMobile = null;
      this._updateJoystickVisibility();
      this.render();
      return;
    }
    const wall = nearest.wall;
    const room = nearest.room;
    const wallLen = this.getWallLength(wall);
    const doorWidthFeet = this.doorWidth / 12;
    const halfParam = wallLen > 0 ? (doorWidthFeet / 2) / wallLen : 0;
    const param = this.getWallParam(world, wall);
    // Clamp to center-valid range so door doesn't extend past wall ends
    let snappedParam = this.snapParamToGrid(wall, param);
    snappedParam = Math.max(halfParam, Math.min(1 - halfParam, snappedParam));
    const snapPoint = this.paramToPoint(wall, snappedParam);
    // Re-center pan so snap point lands at target screen position
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    this.panX = target.x - snapPoint.x * scale;
    this.panY = target.y - snapPoint.y * scale;
    this.mouseWorldRaw = { x: snapPoint.x, y: snapPoint.y };
    this.mouseWorld = { x: snapPoint.x, y: snapPoint.y };
    this._doorMobile = { phase: 1, wall, room, param: snappedParam, point: { x: snapPoint.x, y: snapPoint.y } };
    // Start intro animation
    this._rectIntroAnimating = true;
    this._rectIntroAnimStart = performance.now();
    this._animateRectIntro();
    // Show joystick immediately with wall-aligned arrows
    this._updateJoystickVisibility();
    this._setJoystickArrowsDirs(this._getDoorWallArrows(wall));
    this._startArrowPulse(3000);
    this._startCheckPulse(3000);
    this._updateFloatingCancel();
    this._updateFloatingRepeat();
    this.updateStatus('Slide door along wall, then tap \u2713.');
    this.render();
  }

  _mobileObjectAutoPlace() {
    // Clear stale inactive class from checkmark (may linger from previous tool, e.g. rect drawing)
    const placeBtn = document.querySelector('.ring-center[data-dir="place"]');
    if (placeBtn) placeBtn.classList.remove('inactive');
    // Auto-place a rectangle/circle preview at the center of the screen
    const target = this._getMobileDotScreenPos();
    const world = this.screenToWorld(target.x, target.y);
    const snapped = this.snapToGrid(world.x, world.y);
    // Re-center pan so the snapped grid point lands exactly at the target pixel
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    this.panX = target.x - snapped.x * scale;
    this.panY = target.y - snapped.y * scale;
    this.mouseWorldRaw = { x: snapped.x, y: snapped.y };
    this.mouseWorld = { x: snapped.x, y: snapped.y };
    this.mouseScreen = { x: target.x, y: target.y };
    // Start intro animation
    this._rectIntroAnimating = true;
    this._rectIntroAnimStart = performance.now();
    this._animateRectIntro();
    // Show joystick with all arrows enabled, checkmark ready to place
    this._updateJoystickVisibility();
    this._startArrowPulse(3000);
    this._startCheckPulse(3000);
    this._updateFloatingCancel();
    this._updateFloatingRepeat();
    const label = this.objectType === 'circle' ? 'pillar' : 'column';
    this.updateStatus(`Move ${label} with arrows or tap to reposition, then tap \u2713 to place.`);
  }

  _animateRectIntro() {
    const duration = 400;
    const tick = () => {
      if (!this._rectIntroAnimating) return;
      const elapsed = performance.now() - this._rectIntroAnimStart;
      if (elapsed >= duration) {
        this._rectIntroAnimating = false;
        this.render();
        return;
      }
      this.render();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  _animateRectLock(corner, color) {
    this._rectLockAnimating = true;
    this._rectLockAnimStart = performance.now();
    this._rectLockAnimCorner = { x: corner.x, y: corner.y };
    this._rectLockAnimColor = color || null; // null = default green
    const duration = 400;
    const tick = () => {
      if (!this._rectLockAnimating) return;
      const elapsed = performance.now() - this._rectLockAnimStart;
      if (elapsed >= duration) {
        this._rectLockAnimating = false;
        this._rectLockAnimCorner = null;
        this.render();
        return;
      }
      this.render();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /* ── JOYSTICK VISIBILITY ── */

  _updateJoystickVisibility() {
    if (!this._joystickEl) return;
    // Joystick is mobile-only — never show on desktop
    if (!this._isMobileToolbar) { this._joystickEl.classList.remove('visible'); return; }
    const hasSelection = this.selectedWalls.size > 0 || this.selectedDoors.size > 0 || this.selectedObjects.size > 0;
    const showForSelect = this.activeTool === 'select' && hasSelection &&
      (this._selectHandleMode !== null || this._shouldAutoJoystick() === 'wholeMove');
    // Pen: only show joystick after user has placed a starting point
    const showForPen = this.activeTool === 'pen' && this.hasPlacedFirstPoint && this.currentWall;
    // Rectangle: show joystick when any corner is active (auto-placed or stretching)
    const showForRect = this.activeTool === 'pen' && this.penDrawMode === 'rectangle' &&
      (this._rectCorner1 || this._rectCorner2);
    // Door mobile: show joystick when phase >= 1
    const showForDoorMobile = this.activeTool === 'door' && this._doorMobile && this._doorMobile.phase >= 1;
    // Door/objects: only show after user has tapped grid (mouseWorld set from tap) — but not when _doorMobile is active (it manages its own visibility)
    const showForDoor = this.activeTool === 'door' && !this._doorMobile && this.mouseWorldRaw;
    this._joystickEl.classList.toggle('visible', showForSelect || showForPen || showForRect || showForDoor || showForDoorMobile);
  }

}

window.RoomDesignerMobile = RoomDesignerMobile;

